import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search,
  Download,
  Eye,
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin,
  Phone,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  PieChart,
  MoreHorizontal,
  Edit,
  Trash2,
  Briefcase,
  Plus,
  Save,
  X,
  Upload,
  ChevronUp,
  ChevronDown,
  User,
  List,
  RefreshCw,
  Mail,
  History,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Link,
  Minus,
  RotateCcw,
  Shield,
} from 'lucide-react';
import {
  Box, Card, CardContent, Typography, Grid, Button, FormControl,
  Select, MenuItem, CircularProgress
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import ExportButtons from './btns/ExportButtons';
import { useTheme } from '@mui/material/styles';
import {
  Doughnut, Bar
} from 'react-chartjs-2';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import CandidateOverviewTable from './CandidateOverviewTable';
import EmployeeCard from '../views/dashboard/Default/EmployeeCard';
import { TextField } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { toast, ToastContainer } from 'react-toastify';
import { useLocation, useSearchParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import Table1_2 from './Table1_2';
import CandidateInterviewAnalyticsDashboard from './CandidateInterviewAnalyticsDashboard';
import DateFormatter from './DateFormatter';
import theme from '../assets/scss/_themes-vars.module.scss';
import { format, differenceInYears } from 'date-fns';
import { API_URL } from '../config';
import RichTextEditor from './RichTextEditor';
import { enUS as en } from 'date-fns/locale';
import {
  getAllCandidates,
  addCandidate,
  updateCandidateStatus,
  deleteCandidate,
  getDepartments,
  updateCandidate,
  createOnboarding,
  getCandidateImage,
  getCandidateResume,
  getInProgressOnboardings,
} from '../services/onboardingapi';
import { getAllInterviews, createInterview, updateInterview, deleteInterview } from '../services/interviewapi';
import { getInterviewers } from '../services/EmployeeService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import marabesLogo from '../assets/images/logos/Marabes.png';
import StatusHistoryTimeline from './StatusHistoryTimeline';
import PageHeader from './headers/Header';
import { useOffices } from '../offices';
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);
const THEME_PRIMARY = '#0EA5E9';
const THEME_SECONDARY = '#0284C7';
const SKY_PRIMARY = '#93C5FD';
const SKY_SECONDARY = '#60A5FA';
const rowsPerPage = 10;
const positionOptions = [
  'HR Manager', 'Project Manager', 'Customer Service', 'HR', 'Manager',
  'Account Manager', 'Quality Reviewer', 'Bookkeeper', 'Planner', 'Marketing',
  'Personal Assistance', 'Software Engineer', 'Client Adviser', 'IT Professional'
];
const candidateStatusOptions = [
  'Not Planned Yet', 'Not Attended', 'Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview',
  'Hired', 'Declined by HR', 'Declined by Manager', 'Reserved List', 'Candidate Declined the Offer', 'Postponed',
];
const candidateLevelOptions = ['HIGH', 'AVERAGE', 'LOW'];
const diplomaOptions = ['High School Diploma', 'Specialized Technician', 'Bachelor', 'Master', 'Engineer', 'PHD'];
const appliedFromOptions = ['Email', 'Indeed', 'LinkedIn'];
const interviewStatusOptions = [
  { label: 'Scheduled', value: 'SCHEDULED' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Postponed', value: 'POSTPONED' },
];
const priorityOptions = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const interviewTypeOptions = [
  { value: 'PHONE_SCREEN', label: 'Phone Screen' },
  { value: 'TECHNICAL', label: 'Technical' },
  { value: 'BEHAVIORAL', label: 'Behavioral' },
  { value: 'HR', label: 'HR' },
];
const roundOptions = [
  { value: 'FIRST', label: 'First Interview' },
  { value: 'SECOND', label: 'Second Interview' },
  { value: 'THIRD', label: 'Third Interview' },
];
const toLocalISOString = (localDateTimeString) => {
  if (!localDateTimeString) return null;
  const date = new Date(localDateTimeString);
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
};
const getDisplayValue = (options, value) => {
  const option = options.find((opt) => opt.value === value);
  return option ? option.label : value;
};
const getMonthYear = (dateString) => {
  const date = dateString ? new Date(dateString) : new Date();
  return format(date, 'MMMM yyyy', { locale: en });
};
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};
const getCurrentMonthYear = () => {
  const date = new Date();
  return format(date, 'MMMM yyyy', { locale: en });
};
const getStatusBadgeStyles = (status) => {
  const config = {
    Hired: { className: 'status-approved', icon: CheckCircle, bgColor: 'rgba(16, 185, 129, 0.12)', color: '#166534', border: '#22C55E' },
    'Declined by HR': { className: 'status-rejected', icon: XCircle, bgColor: 'rgba(239, 68, 68, 0.12)', color: '#991B1B', border: '#EF4444' },
    'Declined by Manager': { className: 'status-rejected', icon: XCircle, bgColor: 'rgba(239, 68, 68, 0.12)', color: '#991B1B', border: '#EF4444' },
    'Candidate Declined the Offer': { className: 'status-warning', icon: XCircle, bgColor: 'rgba(245, 158, 11, 0.12)', color: '#92400E', border: '#F59E0B' },
    'Not Attended': { className: 'status-warning', icon: XCircle, bgColor: 'rgba(245, 158, 11, 0.12)', color: '#92400E', border: '#F59E0B' },
    '1st Interview (Online)': { className: 'status-default', icon: Clock, bgColor: 'rgba(139, 92, 246, 0.12)', color: '#5B21B6', border: '#8B5CF6' },
    'Postponed': { className: 'status-warning', icon: Clock, bgColor: 'rgba(245, 158, 11, 0.12)', color: '#92400E', border: '#F59E0B' },
  }[status] || { className: 'status-default', icon: Clock, bgColor: 'rgba(156, 163, 175, 0.12)', color: '#374151', border: '#9CA3AF' };
  return config;
};

const getCandidateLevelBadgeStyles = (level, isDarkMode = false) => {
  switch (level) {
    case 'HIGH':
      return {
        label: 'HIGH',
        circle: '🟢',
        color: isDarkMode ? '#86efac' : '#15803d',
        bgColor: isDarkMode ? 'rgba(22, 101, 52, 0.35)' : '#dcfce7',
        border: isDarkMode ? '#16a34a' : '#86efac',
        mark: 'HIGH',
      };
    case 'AVERAGE':
      return {
        label: 'AVERAGE',
        circle: '🟠',
        color: isDarkMode ? '#fdba74' : '#c2410c',
        bgColor: isDarkMode ? 'rgba(194, 65, 12, 0.35)' : '#ffedd5',
        border: isDarkMode ? '#ea580c' : '#fdba74',
        mark: 'AVERAGE',
      };
    case 'LOW':
      return {
        label: 'LOW',
        circle: '🟡',
        color: isDarkMode ? '#fef08a' : '#854d0e',
        bgColor: isDarkMode ? 'rgba(161, 98, 7, 0.35)' : '#fef9c3',
        border: isDarkMode ? '#ca8a04' : '#fde047',
        mark: 'LOW',
      };
    default:
      return null;
  }
};

const renderCandidateLevelBadge = (level, isDarkMode = false) => {
  if (!level) return null;
  const badge = getCandidateLevelBadgeStyles(level, isDarkMode);
  if (!badge) return null;
  const { label, circle, color, bgColor, border } = badge;
  return (
    <span
      className="candidate-level-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        padding: '0.15rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.72rem',
        fontWeight: '700',
        backgroundColor: bgColor,
        color: color,
        border: `1px solid ${border}`,
        letterSpacing: '0.025em',
        whiteSpace: 'nowrap',
      }}
      title={`Candidate Level: ${label}`}
    >
      <span style={{ fontSize: '0.75rem', lineHeight: 1 }}>{circle}</span>
      {label}
    </span>
  );
};

// HTML plain text reader/converter helper for exports (strips HTML markup and preserves readable text)
const stripHtml = (html) => {
  if (!html) return '';
  if (typeof html !== 'string') return String(html);
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const lines = [];
  const walk = (node) => {
    if (node.nodeType === 3) {
      const t = node.nodeValue ? node.nodeValue.replace(/\u00A0/g, ' ') : '';
      if (t) lines.push(t);
    } else if (node.nodeType === 1) {
      const tag = node.tagName.toUpperCase();
      if (tag === 'BR') { lines.push('\n'); return; }
      if (tag === 'LI') lines.push('• ');
      node.childNodes.forEach(walk);
      if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'TR'].includes(tag)) lines.push('\n');
    }
  };
  tmp.childNodes.forEach(walk);
  const text = lines.join('').replace(/\n{3,}/g, '\n\n').trim();
  return text || (tmp.textContent || tmp.innerText || '').trim();
};

const toPlainText = (value, fallback = 'N/A') => {
  if (value === null || value === undefined) return fallback;
  const text = stripHtml(value);
  return text || fallback;
};

const StatusDistribution = ({ currentData }) => {
  // ✅ Changed: Now accepts 'currentData' prop directly instead of calculating it from an array
  const statuses = ['Not Planned Yet', 'Not Attended', 'Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview', 'Hired', 'Declined by HR', 'Declined by Manager', 'Reserved List'];
  const statusValues = [
    currentData.notPlannedYet || 0, currentData.notAttended || 0, currentData.pending || 0,
    currentData.interview1 || 0,
    currentData.interview2 || 0, currentData.interview3 || 0, currentData.hired || 0,
    currentData.declinedHR || 0, currentData.declinedManager || 0, currentData.reservedList || 0,
  ];

  const filteredLabels = statuses.filter((_, i) => statusValues[i] > 0);
  const filteredData = statusValues.filter(val => val > 0);
  const filteredColors = ['#93C5FD', '#FBBF24', '#F59E0B', '#F97316', '#0EA5E9', '#0284C7', '#16A34A', '#DC2626', '#EF4444', '#8B5CF6'].filter((_, i) => statusValues[i] > 0);

  const data = {
    labels: filteredLabels,
    datasets: [{ data: filteredData, backgroundColor: filteredColors, borderColor: '#ffffff', borderWidth: 2, hoverOffset: 12 }],
  };

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 11 }, color: isDarkMode ? '#e5e7eb' : '#374151', padding: 12, usePointStyle: true } },
      tooltip: { backgroundColor: isDarkMode ? '#374151' : '#1f2937', titleColor: '#fff', bodyColor: '#f9fafb', padding: 10, cornerRadius: 8 },
    },
    cutout: '65%',
  };

  return (
    <div className={`glass-card ${isDarkMode ? 'dark-mode-card' : ''}`} style={{ padding: '1.4rem', borderRadius: '1rem', boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)', backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff', border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gray-200)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h3 className="gradient-text" style={{ fontSize: '1.05rem', fontWeight: '800', margin: 0, color: isDarkMode ? '#f9fafb' : 'inherit' }}>Status Distribution</h3>
          <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#9ca3af' : '#6B7280', margin: '0.25rem 0 0' }}>Interview stages</p>
        </div>
        <PieChart size={18} color={isDarkMode ? '#9ca3af' : THEME_PRIMARY} />
      </div>
      <div style={{ height: '240px' }}>
        {filteredData.length > 0 ? <Doughnut data={data} options={options} /> : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>No data</div>}
      </div>
    </div>
  );
};
// 1. Replace your existing MonthlyApplicationsChart with this:
const MonthlyApplicationsChart = ({ currentData, departments, selectedOffice }) => {
  // ❌ REMOVED: const currentData = monthlyData.length > 0 ? monthlyData[monthlyData.length - 1] : {};
  // ✅ NOW: It uses the 'currentData' prop passed directly from the parent, which is filtered by date.

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const departmentNames = departments.map(dept => dept.name);
  const departmentColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1', '#84CC16', '#F97316'];

  // Calculate total for the SELECTED month/year
  const totalForMonth = departmentNames.reduce((sum, dept) => sum + (currentData[dept] || 0), 0);

  const chartData = {
    labels: departmentNames,
    datasets: [{
      label: 'Applications',
      // Map data from the selected month object
      data: departmentNames.map(dept => currentData[dept] || 0),
      backgroundColor: departmentColors.slice(0, departmentNames.length),
      borderColor: departmentColors.slice(0, departmentNames.length).map(color => color),
      borderWidth: 1,
      borderRadius: 4,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: isDarkMode ? '#374151' : 'rgba(0, 0, 0, 0.8)', titleColor: '#fff', bodyColor: '#fff' },
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0, color: isDarkMode ? '#9ca3af' : '#6b7280' }, grid: { color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' } },
      x: { ticks: { color: isDarkMode ? '#9ca3af' : '#6b7280' }, grid: { display: false } }
    },
  };

  return (
    <div className={`glass-card ${isDarkMode ? 'dark-mode-card' : ''}`} style={{ padding: '1.4rem', borderRadius: '1rem', boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)', backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff', border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gray-200)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h3 className="gradient-text" style={{ fontSize: '1.05rem', fontWeight: '800', margin: 0, color: isDarkMode ? '#f9fafb' : 'inherit' }}>Applications</h3>
          <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#9ca3af' : '#6B7280', margin: '0.25rem 0 0' }}>
            Applications by department {selectedOffice !== 'all' ? `for ${selectedOffice}` : ''}
          </p>
        </div>
        <BarChart3 size={18} color={isDarkMode ? '#9ca3af' : THEME_PRIMARY} />
      </div>
      <div style={{ height: '280px', marginTop: '1rem' }}>
        {departmentNames.length > 0 ? <Bar data={chartData} options={chartOptions} /> : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF' }}>No department data</div>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.2rem', borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)', paddingTop: '0.8rem' }}>
        <span style={{ fontWeight: '700', fontSize: '0.95rem', color: isDarkMode ? '#f9fafb' : '#374151' }}>Total Applications</span>
        <span className="gradient-text" style={{ fontWeight: '800', fontSize: '1.1rem', color: isDarkMode ? '#f9fafb' : 'inherit' }}>{totalForMonth}</span>
      </div>
    </div>
  );
};
const getTabDisplayName = (tab) => {
  const names = {
    all: 'All', notplanned: 'Not Planned', notattended: 'Not Attended', approved: 'Hired',
    pending: 'Pending', rejected: 'Declined', reserved: 'Reserved', late: 'Not Planned-Old', new: 'Not Planned-Recent',
    planned: 'Planned Interviews', postponed: 'Postponed',
  };
  return names[tab] || 'Candidates';
};
const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.warn("Could not load image (HTTP status not OK):", url);
      return null;
    }
    const contentType = res.headers.get("content-type");
    if (!contentType || !contentType.startsWith("image/")) {
      console.warn("Could not load image (non-image content type):", url, contentType);
      return null;
    }
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Could not load image for PDF:", url, error);
    return null;
  }
};
const Candidates = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { offices } = useOffices();

  const officeOptions = useMemo(() => {
    if (!Array.isArray(offices)) return [];
    return offices.map((o) => (typeof o === 'string' ? o : o.name)).filter(Boolean);
  }, [offices]);

  const locationOptions = useMemo(() => {
    const dynamicLocations = officeOptions.map((off) => off.toUpperCase());
    if (!dynamicLocations.includes('ONLINE')) {
      dynamicLocations.push('ONLINE');
    }
    return dynamicLocations.length > 0 ? dynamicLocations : ['NADOR', 'TANGIER', 'ONLINE'];
  }, [officeOptions]);

  const [activeTab, setActiveTab] = useState('all');
  const overviewRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);

  // ✅ ADD THIS LINE HERE:
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [selectedDateFilter, setSelectedDateFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [declinedSubFilter, setDeclinedSubFilter] = useState(null);
  const [hiredSubFilter, setHiredSubFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedOfficeFilter, setSelectedOfficeFilter] = useState('all');
  const [selectedLevelFilter, setSelectedLevelFilter] = useState('all');
  const [selectedAgeFilter, setSelectedAgeFilter] = useState('all');

  const hasActiveCandidateFilters = Boolean(
    searchTerm ||
    selectedFilter !== 'all' ||
    selectedAgeFilter !== 'all' ||
    selectedOfficeFilter !== 'all' ||
    activeTab !== 'all' ||
    selectedLevelFilter !== 'all' ||
    selectedDateFilter
  );

  const resetCandidateFilters = () => {
    setSearchTerm('');
    setSelectedFilter('all');
    setSelectedAgeFilter('all');
    setSelectedOfficeFilter('all');
    setActiveTab('all');
    setSelectedLevelFilter('all');
    setNotPlannedSubFilter(null);
    setDeclinedSubFilter(null);
    setPlannedSubFilter(null);
    setSelectedDateFilter(null);
    setCurrentPage(1);
  };

  const [viewMode, setViewMode] = useState('table');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidatesList, setCandidatesList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAuditInfo, setShowAuditInfo] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', phone: '', position: '', departmentName: '',
    office: '', city: '', cin: '', description: '', image: null, resume: null, status: 'Not Planned Yet',
    level: '',
    experience: '', diploma: '', appliedFrom: '', portfolioLink: '', resumeTeamsLink: '', internship: '',
    skills: [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [profileImages, setProfileImages] = useState({});
  const [interviewsData, setInterviewsData] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [formDataInterview, setFormDataInterview] = useState({
    candidateId: '', interviewerId: '', dateTime: '', duration: 60, type: 'PHONE_SCREEN',
    status: 'SCHEDULED', priority: 'MEDIUM', location: 'NADOR', round: 'FIRST', notes: '', feedback: '',
  });
  const [editingInterview, setEditingInterview] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [inProgressOnboardings, setInProgressOnboardings] = useState([]);
  const [onboardingSearchTerm, setOnboardingSearchTerm] = useState('');
  const [onboardingOfficeFilter, setOnboardingOfficeFilter] = useState('all');
  const allCandidatesRef = useRef(null);
  const dashboardSectionRef = useRef(null);
  const chartsSectionRef = useRef(null);
  const autoDeletionHistory = useRef([]);
  const [mainTab, setMainTab] = useState('overview');
  const [notPlannedSubFilter, setNotPlannedSubFilter] = useState(null);
  const [plannedSubFilter, setPlannedSubFilter] = useState(null);
  const handleExportOverviewPDF = async () => {
    if (!overviewRef.current) return;
    setIsExporting(true);
    try {
      const element = overviewRef.current;
      // Capture the overview section
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f8fafc' // Matches app background
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const today = new Date().toLocaleDateString('en-GB');
      // --- Draw Header Style (Matches HRWeeklyReport) ---
      const axoLogoUrl = '/logo1.png'; // Ensure this file exists in public folder
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);
      try {
        pdf.addImage(marabesLogo, 'PNG', margin, 10, 35, 12);
      } catch (e) {
        console.warn("Marabes logo load failed", e);
      }
      if (axoBase64) {
        try {
          pdf.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12);
        } catch (e) {
          console.warn("AXO logo add failed", e);
        }
      }
      // Blue Divider Line & Background
      pdf.setDrawColor(63, 81, 181); // Primary Blue
      pdf.setLineWidth(0.5);
      pdf.line(margin, 25, pageWidth - margin, 25);
      pdf.setFillColor(232, 245, 255); // Light Blue Background
      pdf.rect(margin, 30, contentWidth, 14, 'F');
      const textY = 39;
      // Header Text
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Date: ${today}`, margin + 3, textY);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(40, 58, 139); // Darker Blue
      pdf.text('Candidates Overview Report', pageWidth / 2, textY, { align: 'center' });
      // --- Add Captured Dashboard Image ---
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(contentWidth / imgWidth, (pageHeight - 50) / imgHeight);
      const imgX = (pageWidth - imgWidth * ratio) / 2;
      const imgY = 50;
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`candidate-overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export overview.");
    } finally {
      setIsExporting(false);
    }
  };
  const fourteenDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d;
  }, []);
  const formatAuditDate = (dateString) => {
    if (!dateString) return '09/09/2025';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '09/09/2025';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const transformStatus = (apiStatus) => {
    const statusMap = {
      NOT_PLANNED_YET: 'Not Planned Yet', NOT_ATTENDED: 'Not Attended', PENDING: 'Pending',
      INTERVIEW_1: '1st Interview (Online)',
      INTERVIEW_2: '2nd Interview', INTERVIEW_3: '3rd Interview', HIRED: 'Hired',
      DECLINED_BY_HR: 'Declined by HR', DECLINED_BY_MANAGER: 'Declined by Manager',
      RESERVED_LIST: 'Reserved List', CANDIDATE_DECLINED_THE_OFFER: 'Candidate Declined the Offer',
      POSTPONED: 'Postponed',
    };
    return statusMap[apiStatus] || 'Not Planned Yet';
  };
  const reverseTransformStatus = (frontendStatus) => {
    const reverseStatusMap = {
      'Not Planned Yet': 'NOT_PLANNED_YET', 'Not Attended': 'NOT_ATTENDED', 'Pending': 'PENDING',
      '1st Interview (Online)': 'INTERVIEW_1',
      '2nd Interview': 'INTERVIEW_2', '3rd Interview': 'INTERVIEW_3', 'Hired': 'HIRED',
      'Declined by HR': 'DECLINED_BY_HR', 'Declined by Manager': 'DECLINED_BY_MANAGER',
      'Reserved List': 'RESERVED_LIST', 'Candidate Declined the Offer': 'CANDIDATE_DECLINED_THE_OFFER',
      'Postponed': 'POSTPONED',
    };
    return reverseStatusMap[frontendStatus] || 'NOT_PLANNED_YET';
  };

  const getCandidateSaveErrorMessage = (error) => {
    const rawData = error?.response?.data;
    const backendMessage = typeof rawData === 'string'
      ? rawData
      : rawData?.message
        || rawData?.error
        || rawData?.detail
        || error?.message;

    if (typeof backendMessage === 'string') {
      const normalizedMessage = backendMessage.trim();
      if (!normalizedMessage) {
        return 'Failed to save candidate. Please check the input data and try again.';
      }

      if (normalizedMessage.includes('Cannot add more than 2 candidates')) {
        return 'Cannot add more than 2 candidates with the same first name and last name.';
      }

      if (normalizedMessage.includes('same first name and last name')) {
        return 'A candidate with the same first name and last name already exists.';
      }

      return normalizedMessage;
    }

    return 'Failed to save candidate. Please check the input data and try again.';
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'all';
    const officeFromUrl = searchParams.get('office') || 'all';
    const levelFromUrl = searchParams.get('level') || 'all';
    const ageFromUrl = searchParams.get('age') || 'all';
    const pageFromUrl = parseInt(searchParams.get('page')) || 1; // ✅ Get Page
    if (['all', 'new', 'notplanned', 'notattended', 'approved', 'pending', 'rejected', 'declinedHR', 'declinedManager', 'reserved', 'late', 'planned', 'postponed'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else {
      setActiveTab('all');
    }
    setSelectedOfficeFilter(officeFromUrl);
    if (candidateLevelOptions.includes(levelFromUrl) || levelFromUrl === 'all') {
      setSelectedLevelFilter(levelFromUrl);
    } else {
      setSelectedLevelFilter('all');
    }
    if (ageFromUrl) {
      setSelectedAgeFilter(ageFromUrl);
    }
    setCurrentPage(pageFromUrl); // ✅ Set initial page
  }, []); // Run once on mount
  // ✅ Update URL when tab, office, level, age, or page changes
  useEffect(() => {
    const params = {
      tab: activeTab,
      office: selectedOfficeFilter,
      page: currentPage
    };
    if (selectedLevelFilter !== 'all') {
      params.level = selectedLevelFilter;
    }
    if (selectedAgeFilter !== 'all') {
      params.age = selectedAgeFilter;
    }
    setSearchParams(params);
  }, [activeTab, selectedOfficeFilter, selectedLevelFilter, selectedAgeFilter, currentPage, setSearchParams]);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [candidatesResponse, departmentsResponse, interviewersResponse, interviewsResponse, onboardingsResponse] = await Promise.all([
        getAllCandidates(),
        getDepartments(),
        getInterviewers().catch(() => []),
        getAllInterviews(),
        getInProgressOnboardings(),
      ]);
      let candidatesData = candidatesResponse.data || candidatesResponse;
      let departmentsData = departmentsResponse.data || departmentsResponse;
      setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
      const transformedCandidates = candidatesData.map((candidate) => {
        let dept = null;
        const deptName = candidate.departmentName;

        // Backend now sends only department name as string, find matching department in our data
        if (deptName && typeof deptName === 'string') {
          const found = departmentsData.find(
            d => d.name.toLowerCase() === deptName.toLowerCase() || d.departmentId === deptName
          );
          if (found) {
            dept = { departmentId: found.departmentId, name: found.name };
          } else {
            // If no exact match found, create dept object with name only
            dept = { name: deptName };
          }
        }

        return {
          ...candidate,
          id: candidate.candidateId,
          status: transformStatus(candidate.status),
          level: candidate.level || null,
          createdAt: candidate.createdDate || candidate.createdAt || new Date().toISOString(),
          hasImage: !!candidate.image,
          hasResume: !!candidate.resume,
          createdBy: candidate.createdBy || 'SYSTEM',
          createdDate: candidate.createdDate,
          lastModifiedBy: candidate.lastModifiedBy || candidate.createdBy || 'SYSTEM',
          lastModifiedDate: candidate.lastModifiedDate || candidate.createdDate,
          onboardingProgress: candidate.onboardingProgress || 0,
          forceStartWorking: candidate.forceStartWorking || false,
          department: dept,
          skills: candidate.skills || [],
        };
      });
      setCandidatesList(transformedCandidates);
      setInProgressOnboardings(onboardingsResponse.data || onboardingsResponse);
      const interviewersArr = Array.isArray(interviewersResponse)
        ? interviewersResponse
        : Array.isArray(interviewersResponse?.data)
          ? interviewersResponse.data
          : [];
      setInterviewers(interviewersArr);
      let interviews = Array.isArray(interviewsResponse) ? interviewsResponse : interviewsResponse.data || [];
      const enrichedInterviews = interviews.map((interview) => {
        const interviewer = interviewersArr.find((emp) => emp.employeeId === interview.interviewerId);
        return {
          ...interview,
          interviewerName: interviewer ? `${interviewer.firstName} ${interviewer.lastName}` : 'Unknown',
        };
      });
      setInterviewsData(enrichedInterviews);
      const imagePromises = transformedCandidates
        .filter((candidate) => candidate.hasImage)
        .map(async (candidate) => {
          try {
            const imageUrl = await getCandidateImage(candidate.id);
            return { id: candidate.id, imageUrl };
          } catch (err) {
            console.error(`Failed to load image for candidate ${candidate.id}:`, err);
            return { id: candidate.id, imageUrl: null };
          }
        });
      const images = await Promise.all(imagePromises);
      const imageMap = images.reduce((acc, { id, imageUrl }) => {
        acc[id] = imageUrl;
        return acc;
      }, {});
      setProfileImages(imageMap);
    } catch (error) {
      console.error('Error fetching ', error);
      setError('Failed to load data. Please try again later.');
      setCandidatesList([]);
      setDepartments([]);
      setInProgressOnboardings([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
    return () => {
      Object.values(profileImages).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);
  const handleRefresh = async () => {
    await fetchData();
    toast.success("Data refreshed successfully");
  };
  const handleOverviewCandidateUpdate = async (candidate, updates) => {
    const mergedCandidate = { ...candidate, ...updates };
    const candidateData = {
      firstName: mergedCandidate.firstName || '',
      lastName: mergedCandidate.lastName || '',
      phone: mergedCandidate.phone || '',
      position: mergedCandidate.position || '',
      department: mergedCandidate.department?.departmentId
        ? { departmentId: mergedCandidate.department.departmentId }
        : mergedCandidate.department || null,
      office: mergedCandidate.office || '',
      city: mergedCandidate.city || '',
      cin: mergedCandidate.cin || '',
      description: mergedCandidate.description || '',
      status: reverseTransformStatus(mergedCandidate.status),
      level: (mergedCandidate.status === 'Hired' || mergedCandidate.status === 'HIRED')
        ? (mergedCandidate.level || null)
        : null,
      experience: mergedCandidate.experience || '',
      diploma: mergedCandidate.diploma || '',
      studyField: mergedCandidate.studyField || '',
      appliedFrom: mergedCandidate.appliedFrom || '',
      portfolioLink: mergedCandidate.portfolioLink || '',
      resumeTeamsLink: mergedCandidate.resumeTeamsLink || '',
      internship: mergedCandidate.internship || '',
      skills: mergedCandidate.skills || candidate.skills || [],
    };

    if (Object.prototype.hasOwnProperty.call(mergedCandidate, 'age')) {
      candidateData.age = mergedCandidate.age === '' || mergedCandidate.age === null
        ? null
        : Number(mergedCandidate.age);
    }

    try {
      const updatedCandidate = await updateCandidate(candidate.id, candidateData);
      setCandidatesList((prev) =>
        prev.map((item) =>
          item.id === candidate.id
            ? {
              ...item,
              ...mergedCandidate,
              ...updatedCandidate,
              id: updatedCandidate.candidateId || candidate.id,
              status: candidateStatusOptions.includes(updatedCandidate.status)
                ? updatedCandidate.status
                : transformStatus(updatedCandidate.status || reverseTransformStatus(mergedCandidate.status)),
              level: updatedCandidate.level !== undefined
                ? updatedCandidate.level
                : ((mergedCandidate.status === 'Hired' || mergedCandidate.status === 'HIRED')
                  ? (mergedCandidate.level || item.level || null)
                  : null),
              hasImage: !!updatedCandidate.image || item.hasImage,
              hasResume: !!updatedCandidate.resume || item.hasResume,
              createdBy: updatedCandidate.createdBy || item.createdBy || 'SYSTEM',
              createdDate: updatedCandidate.createdDate || item.createdDate,
              lastModifiedBy: updatedCandidate.lastModifiedBy || item.lastModifiedBy || 'SYSTEM',
              lastModifiedDate: updatedCandidate.lastModifiedDate || item.lastModifiedDate,
              onboardingProgress: updatedCandidate.onboardingProgress ?? item.onboardingProgress ?? 0,
              forceStartWorking: updatedCandidate.forceStartWorking ?? item.forceStartWorking ?? false,
              department: updatedCandidate.department || item.department,
              skills: updatedCandidate.skills || mergedCandidate.skills || item.skills || [],
            }
            : item
        )
      );
      toast.success('Candidate updated successfully!');
      return updatedCandidate;
    } catch (error) {
      console.error('Error updating candidate from overview:', error);
      toast.error(error.message || 'Failed to update candidate.');
      throw error;
    }
  };
  const deleteDeclinedCandidatesOlderThan6Months = async () => {
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const declinedStatuses = ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer', 'Not Attended'];
      const candidatesToDelete = candidatesList.filter(candidate =>
        declinedStatuses.includes(candidate.status) &&
        new Date(candidate.createdDate) < sixMonthsAgo
      );
      if (candidatesToDelete.length > 0) {
        const deletionPromises = candidatesToDelete.map(candidate =>
          deleteCandidate(candidate.id)
        );
        await Promise.all(deletionPromises);
        setCandidatesList(prev =>
          prev.filter(candidate => !candidatesToDelete.some(c => c.id === candidate.id))
        );
        candidatesToDelete.forEach(candidate => {
          if (profileImages[candidate.id]) {
            URL.revokeObjectURL(profileImages[candidate.id]);
          }
        });
        const deletionRecord = {
          id: Date.now(),
          date: new Date().toISOString(),
          count: candidatesToDelete.length,
          candidates: candidatesToDelete.map(c => ({
            id: c.id,
            name: `${c.firstName} ${c.lastName}`,
            status: c.status,
            createdDate: c.createdDate
          }))
        };
        autoDeletionHistory.current = [deletionRecord, ...autoDeletionHistory.current.slice(0, 9)];
        toast.success(`Deleted ${candidatesToDelete.length} declined candidates older than 6 months.`);
      } else {
        toast.info('No declined candidates older than 6 months found to delete.');
      }
    } catch (error) {
      console.error('Error in manual deletion:', error);
      toast.error('Error in deletion process.');
    }
  };
  const filteredInProgressOnboardings = useMemo(() => {
    let result = [...inProgressOnboardings];
    if (onboardingSearchTerm) {
      const term = onboardingSearchTerm.toLowerCase();
      result = result.filter(
        (o) =>
          `${o.candidate.firstName} ${o.candidate.lastName}`.toLowerCase().includes(term) ||
          o.candidate.office?.toLowerCase().includes(term) ||
          o.candidate.position?.toLowerCase().includes(term) ||
          o.candidate.department?.name?.toLowerCase().includes(term)
      );
    }
    if (onboardingOfficeFilter !== 'all') {
      result = result.filter((o) => o.candidate.office?.toLowerCase() === onboardingOfficeFilter.toLowerCase());
    }
    return result;
  }, [inProgressOnboardings, onboardingSearchTerm, onboardingOfficeFilter]);
  const availableAges = useMemo(() => {
    const ages = new Set();
    candidatesList.forEach((c) => {
      const a = c.age != null && c.age !== '' ? Number(c.age) : null;
      if (a !== null && !isNaN(a) && a > 0) {
        ages.add(a);
      }
    });
    return Array.from(ages).sort((a, b) => a - b);
  }, [candidatesList]);

  const baseFilteredCandidates = useMemo(() => {
    let result = [...candidatesList];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(term) ||
          c.office?.toLowerCase().includes(term) ||
          c.position?.toLowerCase().includes(term) ||
          c.description?.toLowerCase().includes(term) ||
          c.city?.toLowerCase().includes(term) ||
          (c.createdBy && c.createdBy.toLowerCase().includes(term)) ||
          (c.lastModifiedBy && c.lastModifiedBy.toLowerCase().includes(term)) ||
          (c.experience && c.experience.toLowerCase().includes(term)) ||
          (c.diploma && c.diploma.toLowerCase().includes(term)) ||
          (c.appliedFrom && c.appliedFrom.toLowerCase().includes(term)) ||
          (c.age != null && String(c.age).includes(term))
      );
    }
    if (selectedFilter !== 'all') {
      const filterLower = selectedFilter.toLowerCase();
      result = result.filter(
        (c) =>
          c.department?.departmentId?.toLowerCase() === filterLower ||
          c.department?.name?.toLowerCase() === filterLower
      );
    }
    if (selectedAgeFilter !== 'all') {
      result = result.filter((c) => {
        const age = c.age != null && c.age !== '' ? Number(c.age) : null;
        if (selectedAgeFilter === 'not-specified') {
          return age === null || isNaN(age) || age <= 0;
        }
        if (age === null || isNaN(age) || age <= 0) {
          return false;
        }
        if (selectedAgeFilter === 'under-20') return age < 20;
        if (selectedAgeFilter === '20-25') return age >= 20 && age <= 25;
        if (selectedAgeFilter === '26-30') return age >= 26 && age <= 30;
        if (selectedAgeFilter === '31-35') return age >= 31 && age <= 35;
        if (selectedAgeFilter === '36-40') return age >= 36 && age <= 40;
        if (selectedAgeFilter === '41-50') return age >= 41 && age <= 50;
        if (selectedAgeFilter === 'above-50') return age > 50;
        if (selectedAgeFilter.startsWith('exact-')) {
          const targetAge = Number(selectedAgeFilter.replace('exact-', ''));
          return age === targetAge;
        }
        return true;
      });
    }
    if (selectedOfficeFilter !== 'all') {
      result = result.filter((c) => c.office?.toLowerCase() === selectedOfficeFilter.toLowerCase());
    }
    return result;
  }, [candidatesList, searchTerm, selectedFilter, selectedAgeFilter, selectedOfficeFilter]);
  const fetchInterviews = async () => {
    try {
      const interviewsResponse = await getAllInterviews();
      let interviews = Array.isArray(interviewsResponse) ? interviewsResponse : interviewsResponse.data || [];
      const enrichedInterviews = interviews.map((interview) => {
        const interviewer = interviewers.find((emp) => emp.employeeId === interview.interviewerId);
        return {
          ...interview,
          interviewerName: interviewer ? `${interviewer.firstName} ${interviewer.lastName}` : 'Unknown',
        };
      });
      setInterviewsData(enrichedInterviews);
    } catch (error) {
      console.error('Failed to fetch interviews:', error);
      toast.error('Failed to fetch interviews. Please try again.');
    }
  };
  useEffect(() => {
    if (selectedCandidate && selectedCandidate.hasImage) {
      const fetchImage = async () => {
        try {
          const imageUrl = await getCandidateImage(selectedCandidate.id);
          setProfileImageUrl(imageUrl);
        } catch (error) {
          console.error('Error fetching candidate image:', error);
          setProfileImageUrl(null);
        }
      };
      fetchImage();
    } else {
      setProfileImageUrl(null);
    }
    return () => {
      if (profileImageUrl) {
        URL.revokeObjectURL(profileImageUrl);
      }
    };
  }, [selectedCandidate]);
  // 2. Ensure this logic exists inside Candidates component:
  const monthlyData = useMemo(() => {
    const monthsData = {};
    const departmentNames = [...new Set(departments.map((d) => d.name))];

    // Filter by office first
    const filteredCandidates = selectedOfficeFilter === 'all'
      ? candidatesList
      : candidatesList.filter(c => c.office?.toLowerCase() === selectedOfficeFilter.toLowerCase());

    filteredCandidates.forEach((candidate) => {
      // ✅ CRITICAL: This groups by the candidate's CREATION date
      const monthYear = getMonthYear(candidate.createdAt);

      const deptName = candidate.department?.name || 'Unknown';
      if (!monthsData[monthYear]) {
        monthsData[monthYear] = {};
        departmentNames.forEach((dept) => {
          monthsData[monthYear][dept] = 0;
        });
        monthsData[monthYear]['Unknown'] = 0;
      }
      monthsData[monthYear][deptName] = (monthsData[monthYear][deptName] || 0) + 1;
    });

    return Object.entries(monthsData)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => new Date(a.month + ' 1') - new Date(b.month + ' 1'));
  }, [candidatesList, departments, selectedOfficeFilter]);
  const monthlyOverview = useMemo(() => {
    const monthsData = {};
    candidatesList.forEach((candidate) => {
      const monthYear = getMonthYear(candidate.createdAt);
      if (!monthsData[monthYear]) {
        monthsData[monthYear] = {
          notPlannedYet: 0,
          notAttended: 0,
          pending: 0,
          interview1: 0,
          interview2: 0,
          interview3: 0,
          hired: 0,
          declinedHR: 0,
          declinedManager: 0,
          reservedList: 0,
          total: 0,
        };
      }
      const statusKey = {
        'Not Planned Yet': 'notPlannedYet',
        'Not Attended': 'notAttended',
        'Pending': 'pending',
        '1st Interview (Online)': 'interview1',
        '2nd Interview': 'interview2',
        '3rd Interview': 'interview3',
        'Hired': 'hired',
        'Declined by HR': 'declinedHR',
        'Declined by Manager': 'declinedManager',
        'Reserved List': 'reservedList',
        'Candidate Declined the Offer': 'candidateDeclined',
      }[candidate.status] || 'notPlannedYet';
      monthsData[monthYear][statusKey]++;
      monthsData[monthYear].total++;
    });
    return Object.entries(monthsData)
      .map(([month, data]) => ({ month, ...data }))
      .sort((a, b) => new Date(a.month + ' 1') - new Date(b.month + ' 1'));
  }, [candidatesList]);

  const selectedChartApplicationData = useMemo(() => {
    const data = {};
    baseFilteredCandidates.forEach(candidate => {
      const date = new Date(candidate.createdDate || candidate.createdAt);
      if (isNaN(date) || date.getFullYear() !== selectedYear) return;
      const dept = candidate.department?.name || 'Unknown';
      if (!data[dept]) data[dept] = 0;
      data[dept]++;
    });
    return data;
  }, [baseFilteredCandidates, selectedYear]);

  const selectedChartStatusData = useMemo(() => {
    const data = {
      notPlannedYet: 0,
      notAttended: 0,
      pending: 0,
      interview1: 0,
      interview2: 0,
      interview3: 0,
      hired: 0,
      declinedHR: 0,
      declinedManager: 0,
      reservedList: 0,
      total: 0,
    };
    baseFilteredCandidates.forEach(candidate => {
      const date = new Date(candidate.createdDate || candidate.createdAt);
      if (isNaN(date) || date.getFullYear() !== selectedYear) return;
      const statusKey = {
        'Not Planned Yet': 'notPlannedYet',
        'Not Attended': 'notAttended',
        'Pending': 'pending',
        '1st Interview (Online)': 'interview1',
        '2nd Interview': 'interview2',
        '3rd Interview': 'interview3',
        'Hired': 'hired',
        'Declined by HR': 'declinedHR',
        'Declined by Manager': 'declinedManager',
        'Reserved List': 'reservedList',
        'Candidate Declined the Offer': 'candidateDeclined',
      }[candidate.status] || 'notPlannedYet';

      if (data[statusKey] !== undefined) data[statusKey]++;
      data.total++;
    });
    return data;
  }, [baseFilteredCandidates, selectedYear]);
  const currentMonthData = useMemo(() => {
    const currentMonth = getCurrentMonthYear();
    return (
      monthlyOverview.find((m) => m.month === currentMonth) ||
      monthlyOverview[monthlyOverview.length - 1] || {
        total: 0,
        hired: 0,
        interview1: 0,
        interview2: 0,
        interview3: 0,
        pending: 0,
        reservedList: 0,
      }
    );
  }, [monthlyOverview]);
  const secondInterviews = useMemo(() => {
    return baseFilteredCandidates
      .filter((candidate) => candidate.status === '2nd Interview')
      .map((candidate) => ({
        id: candidate.id,
        name: `${candidate.firstName} ${candidate.lastName}`,
        status: candidate.status,
        location: candidate.office,
        phone: candidate.phone,
        resume: candidate.resume || '',
        reports: '',
        reasonRejection: '',
        role: candidate.position,
        scheduledDate: candidate.createdAt,
      }));
  }, [baseFilteredCandidates]);
  const thirdInterviews = useMemo(() => {
    return baseFilteredCandidates
      .filter((candidate) => candidate.status === '3rd Interview')
      .map((candidate) => ({
        id: candidate.id,
        name: `${candidate.firstName} ${candidate.lastName}`,
        status: candidate.status,
        location: candidate.office,
        phone: candidate.phone,
        resume: candidate.resume || '',
        reports: '',
        reasonRejection: '',
        role: candidate.position,
        scheduledDate: candidate.createdAt,
      }));
  }, [baseFilteredCandidates]);
  const waitingList = useMemo(() => {
    return baseFilteredCandidates
      .filter((candidate) => candidate.status === 'Pending')
      .map((candidate) => ({
        id: candidate.id,
        name: `${candidate.firstName} ${candidate.lastName}`,
        status: candidate.status,
        location: candidate.office,
        phone: candidate.phone,
        resume: candidate.resume || '',
        reports: '',
        role: candidate.position,
        appliedDate: candidate.createdAt,
      }));
  }, [baseFilteredCandidates]);
  const reservedList = useMemo(() => {
    return baseFilteredCandidates
      .filter((candidate) => candidate.status === 'Reserved List')
      .map((candidate) => ({
        id: candidate.id,
        name: `${candidate.firstName} ${candidate.lastName}`,
        status: candidate.status,
        location: candidate.office,
        phone: candidate.phone,
        resume: candidate.resume || '',
        reports: '',
        role: candidate.position,
        appliedDate: candidate.createdAt,
      }));
  }, [baseFilteredCandidates]);
  const filteredCandidates = useMemo(() => {
    let result = [...baseFilteredCandidates];

    // 1. Apply date filter
    if (selectedDateFilter) {
      result = result.filter(candidate => {
        if (['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(candidate.status)) {
          const candidateInterviews = interviewsData.filter(i => i.candidateId === candidate.id && i.status === 'SCHEDULED');
          if (candidateInterviews.length === 0) return false;
          const latest = candidateInterviews.reduce((a, b) => new Date(b.dateTime) > new Date(a.dateTime) ? b : a);
          return isSameDay(latest.dateTime, selectedDateFilter);
        } else {
          return isSameDay(candidate.lastModifiedDate, selectedDateFilter);
        }
      });
    }

    // 2. Apply Tab Filter
    if (activeTab !== 'all') {

      // ✅ UPDATED: "Total Candidates" Click Logic
      if (activeTab === 'total_overview') {
        result = result.filter(c => {
          // 1. Include "Not Planned Yet"
          if (c.status === 'Not Planned Yet') return true;

          // 2. Include "Pending" and all interview stages
          if (['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(c.status)) return true;

          /* // 3. Include ONLY "Hired Pending" (Hired status + actively in onboarding)
          if (c.status === 'Hired') {
            return inProgressOnboardings.some(
              o => o.candidate.candidateId === c.id && o.status === 'IN_PROGRESS'
            );
          } */

          return false;
        });
      }
      else if (activeTab === 'approved') {
        result = result.filter(c => c.status === 'Hired');
        if (hiredSubFilter === 'pending') {
          result = result.filter(c => inProgressOnboardings.some(o => o.candidate.candidateId === c.id && o.status === 'IN_PROGRESS'));
        } else if (hiredSubFilter === 'hired') {
          result = result.filter(c => !inProgressOnboardings.some(o => o.candidate.candidateId === c.id && o.status === 'IN_PROGRESS'));
        }
      }
      else if (activeTab === 'notplanned') {
        result = result.filter(c => c.status === 'Not Planned Yet');
        if (notPlannedSubFilter === 'recent') result = result.filter(c => new Date(c.createdDate) >= fourteenDaysAgo);
        if (notPlannedSubFilter === 'old') result = result.filter(c => new Date(c.createdDate) < fourteenDaysAgo);
      }
      else if (activeTab === 'rejected') {
        const currentYear = new Date().getFullYear();
        const types = declinedSubFilter
          ? (declinedSubFilter === 'hr' ? ['Declined by HR'] : declinedSubFilter === 'manager' ? ['Declined by Manager'] : ['Candidate Declined the Offer'])
          : ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'];
        result = result.filter(c => types.includes(c.status) && new Date(c.lastModifiedDate).getFullYear() === currentYear);
      }
      // else if (activeTab === 'declinedHR' || activeTab === 'declinedManager') {
      //   const currentYear = new Date().getFullYear();
      //   const targetApiStatus = activeTab === 'declinedHR' ? 'DECLINED_BY_HR' : 'DECLINED_BY_MANAGER';
      //   result = result.filter(c => reverseTransformStatus(c.status) === targetApiStatus && new Date(c.lastModifiedDate).getFullYear() === currentYear);
      // }
      else if (activeTab === 'pending') result = result.filter(c => ['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(c.status));
      else if (activeTab === 'notattended') result = result.filter(c => c.status === 'Not Attended');
      else if (activeTab === 'reserved') result = result.filter(c => c.status === 'Reserved List');
      else if (activeTab === 'planned') {
        result = result.filter(c => ['1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(c.status));
        if (plannedSubFilter === 'first') result = result.filter(c => c.status === '1st Interview (Online)');
        else if (plannedSubFilter === 'second') result = result.filter(c => c.status === '2nd Interview');
      }
      else if (activeTab === 'postponed') result = result.filter(c => c.status === 'Postponed');
    }

    // 2.1 Apply Level Filter (Only applicable for Hired candidates)
    if (selectedLevelFilter !== 'all') {
      result = result.filter(c => c.status === 'Hired' && c.level === selectedLevelFilter);
    }

    // 3. Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let valA = a[sortConfig.key], valB = b[sortConfig.key];
        // Handle date sorting
        if (sortConfig.key === 'createdDate' || sortConfig.key === 'lastModifiedDate') {
          valA = new Date(valA);
          valB = new Date(valB);
        } else if (sortConfig.key === 'age') {
          valA = valA != null && valA !== '' ? Number(valA) : -1;
          valB = valB != null && valB !== '' ? Number(valB) : -1;
        }
        return valA < valB ? (sortConfig.direction === 'ascending' ? -1 : 1) : (sortConfig.direction === 'ascending' ? 1 : -1);
      });
    }
    return result.map((c) => ({ ...c, interviews: interviewsData.filter((i) => i.candidateId === c.id) }));
  }, [baseFilteredCandidates, interviewsData, activeTab, selectedLevelFilter, sortConfig, fourteenDaysAgo, inProgressOnboardings, notPlannedSubFilter, declinedSubFilter, hiredSubFilter, plannedSubFilter, selectedDateFilter]);
  const dashboardCardCounts = useMemo(() => {
    const notPlannedCount = baseFilteredCandidates.filter((c) => c.status === 'Not Planned Yet').length;
    const pendingCount = baseFilteredCandidates.filter((c) =>
      ['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(c.status)
    ).length;
    const lateCount = baseFilteredCandidates.filter(
      (c) => c.status === 'Not Planned Yet' && new Date(c.createdDate) < fourteenDaysAgo
    ).length;
    const newCount = notPlannedCount - lateCount;
    const filteredHiredOnboardings = selectedOfficeFilter === 'all'
      ? inProgressOnboardings
      : inProgressOnboardings.filter(
        (onboarding) => onboarding.candidate.office?.toLowerCase() === selectedOfficeFilter.toLowerCase()
      );
    const hiredCount = filteredHiredOnboardings.length;

    // Updated: Filter declined candidates to only include current year data
    // 1. Get Current Year
    const currentYear = new Date().getFullYear();

    // 2. Filter List
    const declinedCount = baseFilteredCandidates.filter((c) =>
      // Check Statuses
      ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'].includes(c.status) &&
      // Check Date (Must be this year)
      new Date(c.lastModifiedDate).getFullYear() === currentYear
    ).length;

    const notAttendedCount = baseFilteredCandidates.filter((c) => c.status === 'Not Attended').length;
    const plannedCount = baseFilteredCandidates.filter((c) =>
      ['1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(c.status)
    ).length;
    const totalCandidatesCount = notPlannedCount + pendingCount;

    return {
      totalCandidates: totalCandidatesCount,
      notPlanned: notPlannedCount,
      notPlannedRecent: newCount,
      notPlannedOld: lateCount,
      pending: pendingCount,
      declined: declinedCount,
      reserved: baseFilteredCandidates.filter((c) => c.status === 'Reserved List').length,
      hired: hiredCount,
      notAttended: notAttendedCount,
      planned: plannedCount,
    };
  }, [baseFilteredCandidates, inProgressOnboardings, selectedOfficeFilter, fourteenDaysAgo]);
  const paginatedCandidates = useMemo(() => {
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    return filteredCandidates.slice(indexOfFirstRow, indexOfLastRow);
  }, [filteredCandidates, currentPage]);
  const handleSort = (key) => {
    if (key)
      setSortConfig((prev) => ({
        key,
        direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
      }));
  };
  const handleFormSubmit = async () => {
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.phone ||
      !formData.position ||
      !formData.departmentName ||
      !formData.office ||
      !formData.cin ||
      !formData.status
    ) {
      setError('Please fill in all required fields: firstName, lastName, phone, position, department, office, cin, and status.');
      toast.error('Please fill in all required fields.');
      return;
    }
    if (!departments.some((dept) => dept.departmentId === formData.departmentName)) {
      setError('Invalid department selected.');
      toast.error('Invalid department selected.');
      return;
    }
    if (formData.status === 'Hired' && !formData.level) {
      setError('Please select a level (HIGH, AVERAGE, or LOW) for the hired candidate.');
      toast.error('Please select a candidate level for Hired status.');
      return;
    }
    try {
      setError(null);
      const candidateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        position: formData.position,
        department: { departmentId: formData.departmentName },
        office: formData.office,
        city: formData.city,
        cin: formData.cin,
        description: formData.description || '',
        status: reverseTransformStatus(formData.status),
        level: formData.status === 'Hired' ? formData.level : null,
        experience: formData.experience || '',
        diploma: formData.diploma || '',
        studyField: formData.studyField || '',
        appliedFrom: formData.appliedFrom || '',
        portfolioLink: formData.portfolioLink || '',
        resumeTeamsLink: formData.resumeTeamsLink || '',
        internship: formData.internship || '',
        skills: (formData.skills || [])
          .filter((s) => s && s.description && s.description.trim() !== '')
          .map((s) => ({
            ...(s.skillId ? { skillId: s.skillId } : {}),
            type: s.type || 'COMMUNICATION_SKILLS',
            description: s.description.trim(),
          })),
      };
      console.log('Sending candidate ', candidateData);
      let candidateId;
      const newStatus = formData.status;
      let updatedCandidate = null;
      if (editingCandidate) {
        const oldStatus = editingCandidate.status;
        updatedCandidate = await updateCandidate(editingCandidate.id, candidateData, imageFile, resumeFile);
        const imageUrl = updatedCandidate.image && imageFile
          ? await getCandidateImage(updatedCandidate.candidateId)
          : profileImages[editingCandidate.id] || null;
        setCandidatesList((prev) =>
          prev.map((candidate) =>
            candidate.id === editingCandidate.id
              ? {
                ...updatedCandidate,
                id: updatedCandidate.candidateId,
                status: transformStatus(updatedCandidate.status),
                level: updatedCandidate.level !== undefined
                  ? updatedCandidate.level
                  : (formData.status === 'Hired' ? formData.level : null),
                hasImage: !!updatedCandidate.image,
                hasResume: !!updatedCandidate.resume,
                createdBy: updatedCandidate.createdBy || candidate.createdBy || 'SYSTEM',
                createdDate: updatedCandidate.createdDate || candidate.createdDate,
                lastModifiedBy: updatedCandidate.lastModifiedBy || updatedCandidate.createdBy || 'SYSTEM',
                lastModifiedDate: updatedCandidate.lastModifiedDate || updatedCandidate.createdDate,
                onboardingProgress: updatedCandidate.onboardingProgress || 0,
                forceStartWorking: updatedCandidate.forceStartWorking || false,
                skills: updatedCandidate.skills || candidateData.skills || candidate.skills || [],
              }
              : candidate
          )
        );
        setProfileImages((prev) => ({
          ...prev,
          [updatedCandidate.candidateId]: imageUrl,
        }));
        toast.success('Candidate updated successfully!');
        candidateId = updatedCandidate.candidateId;
        // Auto-open interview modal if status changed to interview stage
        if (newStatus !== oldStatus && ['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(newStatus)) {
          let roundValue = 'FIRST';
          if (newStatus === '2nd Interview') roundValue = 'SECOND';
          else if (newStatus === '3rd Interview') roundValue = 'THIRD';
          setFormDataInterview({
            candidateId,
            interviewerId: '',
            dateTime: '',
            duration: 60,
            type: 'PHONE_SCREEN',
            status: 'SCHEDULED',
            priority: 'MEDIUM',
            location: 'NADOR',
            round: roundValue,
            notes: '',
            feedback: '',
          });
          setEditingInterview(null);
          setIsModalOpen(true);
          return;
        }
      } else {
        updatedCandidate = await addCandidate(candidateData, imageFile, resumeFile);
        const imageUrl = updatedCandidate.image ? await getCandidateImage(updatedCandidate.candidateId) : null;
        setCandidatesList((prev) => [
          ...prev,
          {
            ...updatedCandidate,
            id: updatedCandidate.candidateId,
            status: transformStatus(updatedCandidate.status),
            level: updatedCandidate.level !== undefined
              ? updatedCandidate.level
              : (formData.status === 'Hired' ? formData.level : null),
            createdAt: new Date().toISOString(),
            hasImage: !!updatedCandidate.image,
            hasResume: !!updatedCandidate.resume,
            createdBy: updatedCandidate.createdBy || 'SYSTEM',
            createdDate: updatedCandidate.createdDate,
            lastModifiedBy: updatedCandidate.lastModifiedBy || updatedCandidate.createdBy || 'SYSTEM',
            lastModifiedDate: updatedCandidate.lastModifiedDate || updatedCandidate.createdDate,
            onboardingProgress: updatedCandidate.onboardingProgress || 0,
            forceStartWorking: updatedCandidate.forceStartWorking || false,
            skills: updatedCandidate.skills || candidateData.skills || [],
          },
        ]);
        setProfileImages((prev) => ({
          ...prev,
          [updatedCandidate.candidateId]: imageUrl,
        }));
        toast.success('Candidate added successfully!');
        candidateId = updatedCandidate.candidateId;
        // Auto-open interview modal for new candidate with interview status
        if (['Pending', '1st Interview (Online)', '2nd Interview', '3rd Interview'].includes(newStatus)) {
          let roundValue = 'FIRST';
          if (newStatus === '2nd Interview') roundValue = 'SECOND';
          else if (newStatus === '3rd Interview') roundValue = 'THIRD';
          setFormDataInterview({
            candidateId,
            interviewerId: '',
            dateTime: '',
            duration: 60,
            type: 'PHONE_SCREEN',
            status: 'SCHEDULED',
            priority: 'MEDIUM',
            location: 'NADOR',
            round: roundValue,
            notes: '',
            feedback: '',
          });
          setEditingInterview(null);
          setIsModalOpen(true);
          return;
        }
      }
      // ✅ AUTO-CLEANUP INTERVIEWS ONLY ON FORM SAVE — SAFE VERSION
      // Define the mapping of candidate statuses to interview statuses
      const terminalStatusMap = {
        'Hired': 'COMPLETED',
        'Declined by HR': 'COMPLETED',
        'Declined by Manager': 'COMPLETED',
        'Candidate Declined the Offer': 'CANCELLED',
        'Not Attended': 'CANCELLED',
      };
      // Only proceed if this is a terminal status that requires interview cleanup
      const newInterviewStatus = terminalStatusMap[newStatus];
      if (newInterviewStatus) {
        // Get all scheduled interviews for this candidate
        const scheduledInterviews = interviewsData.filter(
          (int) => int.candidateId === candidateId && int.status === 'SCHEDULED'
        );
        if (scheduledInterviews.length > 0) {
          // ✅ SAFE UPDATE: Send minimal payload with all required fields
          const updatePromises = scheduledInterviews.map(async (int) => {
            // Get the full interview details to preserve all required fields
            const fullInterview = interviewsData.find(i => i.interviewId === int.interviewId);
            if (!fullInterview) return null;
            // Create a minimal but complete payload
            const payload = {
              id: int.interviewId, // ✅ Required: Include ID in payload
              candidateId: int.candidateId,
              interviewerId: int.interviewerId,
              dateTime: int.dateTime,
              duration: int.duration,
              type: int.type,
              status: newInterviewStatus, // ✅ Only change the status
              priority: int.priority,
              location: int.location,
              // ✅ CRITICAL: Always include the round field to avoid NullPointerException
              round: int.round,
              notes: int.notes || '',
              feedback: int.feedback || '',
            };
            // Add a meaningful feedback message based on the status change
            if (newStatus === 'Hired') {
              payload.feedback = payload.feedback ?
                `${payload.feedback}
Status updated to Hired - interview marked as completed.` :
                'Status updated to Hired - interview marked as completed.';
            } else {
              payload.feedback = payload.feedback ?
                `${payload.feedback}
Candidate status changed to ${newStatus} - interview cancelled.` :
                `Candidate status changed to ${newStatus} - interview cancelled.`;
            }
            return updateInterview(int.interviewId, payload);
          }).filter(promise => promise !== null);
          if (updatePromises.length > 0) {
            try {
              await Promise.all(updatePromises);
              await fetchInterviews(); // Refresh local state
              toast.info(`Updated ${updatePromises.length} interview(s) to ${newInterviewStatus}`);
            } catch (error) {
              console.error('Failed to auto-update interviews:', error);
              toast.warning('Some interviews could not be updated. Please review manually.');
              // Detailed error handling
              if (error.response?.data?.message) {
                console.error('Backend error:', error.response.data.message);
                toast.error(`Error: ${error.response.data.message}`);
              }
            }
          }
        }
      }
      // Reset form
      setShowAddForm(false);
      setEditingCandidate(null);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        position: '',
        departmentName: '',
        office: '',
        city: '',
        cin: '',
        description: '',
        image: null,
        resume: null,
        status: 'Not Planned Yet',
        level: '',
        experience: '',
        diploma: '',
        studyField: '',
        appliedFrom: '',
        portfolioLink: '',
        resumeTeamsLink: '',
        internship: '',
        skills: [],
      });
      setImageFile(null);
      setResumeFile(null);
    } catch (error) {
      console.error('Error saving candidate:', error);
      const errorMessage = getCandidateSaveErrorMessage(error);
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };
  const handleEdit = (candidate) => {
    const rawDeptId = candidate.departmentId || candidate.department?.departmentId || '';
    const deptMatch = rawDeptId
      ? departments.find(d => d.departmentId === rawDeptId || d.name?.toLowerCase() === rawDeptId.toLowerCase())
      : null;
    setFormData({
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      phone: candidate.phone,
      position: candidate.position,
      departmentName: deptMatch?.departmentId || rawDeptId,
      office: candidate.office,
      city: candidate.city || '',
      cin: candidate.cin,
      description: candidate.description || '',
      image: candidate.image,
      resume: candidate.resume,
      status: candidate.status || 'Not Planned Yet',
      level: candidate.status === 'Hired' ? (candidate.level || '') : '',
      experience: candidate.experience || '',
      diploma: candidate.diploma || '',
      studyField: candidate.studyField || '',
      appliedFrom: candidate.appliedFrom || '',
      portfolioLink: candidate.portfolioLink || '',
      resumeTeamsLink: candidate.resumeTeamsLink || '',
      internship: candidate.internship || '',
      skills: Array.isArray(candidate.skills)
        ? candidate.skills.map((s) => ({
            skillId: s.skillId || null,
            type: s.type || 'COMMUNICATION_SKILLS',
            description: s.description || '',
          }))
        : [],
      createdBy: candidate.createdBy,
      createdDate: candidate.createdDate,
      lastModifiedBy: candidate.lastModifiedBy,
      lastModifiedDate: candidate.lastModifiedDate,
    });
    setImageFile(null);
    setResumeFile(null);
    setEditingCandidate(candidate);
    setShowAddForm(true);
    setError(null);
  };
  const handleDelete = async (candidateId) => {
    if (window.confirm('Are you sure you want to delete this candidate?')) {
      try {
        await deleteCandidate(candidateId);
        setCandidatesList((prev) => prev.filter((candidate) => candidate.id !== candidateId));
        setProfileImages((prev) => {
          const newImages = { ...prev };
          if (newImages[candidateId]) {
            URL.revokeObjectURL(newImages[candidateId]);
            delete newImages[candidateId];
          }
          return newImages;
        });
        toast.success('Candidate deleted successfully!');
      } catch (error) {
        console.error('Error deleting candidate:', error);
        toast.error(`Failed to delete candidate: ${error.message}`);
        setError(`Failed to delete candidate: ${error.message}`);
      }
    }
  };
  const handleDownloadResume = async (candidateId) => {
    try {
      const resumeBlob = await getCandidateResume(candidateId);
      const url = window.URL.createObjectURL(resumeBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resume_${candidateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Resume downloaded successfully!');
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error(`Failed to download resume: ${error.message}`);
    }
  };

  const handleViewResume = async (candidateId) => {
    try {
      const resumeBlob = await getCandidateResume(candidateId);
      const url = window.URL.createObjectURL(resumeBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing resume:', error);
      toast.error(`Failed to view resume: ${error.message}`);
    }
  };
  const getStatusBadge = (status) => {
    const { className, icon: Icon, bgColor, color, border } = getStatusBadgeStyles(status);
    return (
      <span className={`status-badge ${className}`} style={{ backgroundColor: bgColor, color: color, border: `1px solid ${border}` }}>
        <Icon size={12} />
        {status}
      </span>
    );
  };
  const handleViewDetails = (candidate) => {
    setSelectedCandidate(candidate);
    setShowDetails(true);
    setError(null);
  };
  // ✅ UPDATED: Switch to Candidates tab AND apply filter
  const handleCardClick = (tab, subFilter = null) => {
    setMainTab('candidates');
    setActiveTab(tab);

    // Handle Not Planned sub-filters
    if (tab === 'notplanned' && subFilter !== null) {
      setNotPlannedSubFilter(subFilter);
    }

    // ✅ NEW: Handle Hired sub-filters
    if (tab === 'approved') {
      setHiredSubFilter('pending'); // Always default to 'pending' (Onboarding In Progress) when card is clicked
    }

    // Handle Total Overview
    if (tab === 'total_overview') {
      // logic handled in filteredCandidates
    }

    setCurrentPage(1);
    if (allCandidatesRef.current) {
      allCandidatesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const handleSchedule = (candidate) => {
    if (['Hired', 'Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer', 'Not Attended'].includes(candidate.status)) {
      alert('Cannot schedule an interview for a candidate with this status.');
      return;
    }
    setFormDataInterview({
      candidateId: candidate.id,
      interviewerId: '',
      dateTime: '',
      duration: 60,
      type: 'PHONE_SCREEN',
      status: 'SCHEDULED',
      priority: 'MEDIUM',
      location: 'NADOR',
      round: 'FIRST',
      notes: '',
      feedback: '',
    });
    setEditingInterview(null);
    setIsModalOpen(true);
  };
  const handleInterviewFormSubmit = async () => {
    // Validation
    if (
      !formDataInterview.candidateId ||
      !formDataInterview.interviewerId ||
      !formDataInterview.dateTime ||
      !formDataInterview.round
    ) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (isNaN(new Date(formDataInterview.dateTime))) {
      toast.error('Please enter a valid date and time.');
      return;
    }
    // ✅ STRONGER VALIDATION: Block interviews for terminal-status candidates
    const selectedCandidate = candidatesList.find((c) => c.id === formDataInterview.candidateId);
    const terminalStatuses = [
      'Hired',
      'Declined by HR',
      'Declined by Manager',
      'Candidate Declined the Offer',
      'Not Attended',
    ];
    if (selectedCandidate && terminalStatuses.includes(selectedCandidate.status)) {
      toast.error(`Cannot schedule interviews for candidates with status: ${selectedCandidate.status}`);
      return;
    }
    // ✅ Ensure round is always provided and valid
    if (!formDataInterview.round) {
      toast.error('Please select a valid interview round.');
      return;
    }
    const interviewData = {
      candidateId: formDataInterview.candidateId,
      interviewerId: formDataInterview.interviewerId,
      dateTime: toLocalISOString(formDataInterview.dateTime),
      duration: parseInt(formDataInterview.duration) || 60,
      type: formDataInterview.type,
      status: formDataInterview.status,
      priority: formDataInterview.priority,
      location: formDataInterview.location,
      round: formDataInterview.round, // ✅ Always include round
      notes: formDataInterview.notes || '',
      feedback: formDataInterview.feedback || '',
    };
    try {
      if (editingInterview) {
        await updateInterview(editingInterview.interviewId, interviewData);
      } else {
        await createInterview(interviewData);
      }
      await fetchInterviews();
      setIsModalOpen(false);
      setEditingInterview(null);
      setFormDataInterview({
        candidateId: '',
        interviewerId: '',
        dateTime: '',
        duration: 60,
        type: 'PHONE_SCREEN',
        status: 'SCHEDULED',
        priority: 'MEDIUM',
        location: 'NADOR',
        round: 'FIRST',
        notes: '',
        feedback: '',
      });
      toast.success(editingInterview ? 'Interview updated successfully!' : 'Interview scheduled successfully!');
    } catch (error) {
      console.error('Error submitting interview:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save interview.';
      toast.error(`Error: ${errorMsg}`);
    }
  };
  const handleEditInterview = (interview) => {
    const candidate = candidatesList.find((c) => c.id === interview.candidateId);
    if (candidate && ['Hired', 'Declined by HR', 'Declined by Manager', 'Not Attended'].includes(candidate.status)) {
      alert('Cannot edit an interview for a candidate with this status.');
      return;
    }
    setFormDataInterview({
      candidateId: interview.candidateId,
      interviewerId: interview.interviewerId,
      dateTime: format(new Date(interview.dateTime), "yyyy-MM-dd'T'HH:mm"),
      duration: interview.duration,
      type: interview.type,
      status: interview.status,
      priority: interview.priority,
      location: interview.location,
      round: interview.round,
      notes: interview.notes,
      feedback: interview.feedback,
    });
    setEditingInterview(interview);
    setIsModalOpen(true);
  };
  const handleViewInterview = (interview) => {
    setSelectedInterview({
      ...interview,
      candidateName:
        candidatesList.find((c) => c.id === interview.candidateId)?.firstName +
        ' ' +
        candidatesList.find((c) => c.id === interview.candidateId)?.lastName,
    });
    setIsViewModalOpen(true);
  };
  const handleDeleteInterview = async (id) => {
    if (window.confirm('Are you sure you want to delete this interview?')) {
      try {
        await deleteInterview(id);
        await fetchInterviews();
      } catch (error) {
        console.error('Error deleting interview:', error);
        alert('Failed to delete interview.');
      }
    }
  };
  const handleSendEmail = (interview) => {
    const formattedDate = format(new Date(interview.dateTime), 'dd/MM/yy HH:mm', { locale: en });
    const subject = `Interview Reminder: ${interview.position} - ${interview.candidateName}`;
    const body = `Dear ${interview.candidateName},
This is a reminder for your upcoming interview:
Position: ${interview.position}
Date: ${formattedDate}
Duration: ${interview.duration} minutes
Location: ${interview.location}
Interviewer: ${interview.interviewerName}
Round: ${getDisplayValue(roundOptions, interview.round)}
Best regards,
HR Team`;
    window.open(`mailto:${interview.email ? interview.email : ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };
  const handleReschedule = (interview) => {
    const candidate = candidatesList.find((c) => c.id === interview.candidateId);
    if (candidate?.status === 'Hired') {
      alert('Cannot reschedule an interview for a candidate with Hired status.');
      return;
    }
    setFormDataInterview({
      candidateId: interview.candidateId,
      interviewerId: interview.interviewerId,
      dateTime: format(new Date(interview.dateTime), "yyyy-MM-dd'T'HH:mm"),
      duration: interview.duration,
      type: interview.type,
      status: 'SCHEDULED',
      priority: interview.priority,
      location: interview.location,
      round: interview.round,
      notes: interview.notes,
      feedback: interview.feedback,
    });
    setEditingInterview(interview);
    setIsModalOpen(true);
  };

  // New function to mark interview as completed
  const handleMarkInterviewCompleted = async (interviewId) => {
    try {
      const interview = interviewsData.find(i => i.interviewId === interviewId);
      if (!interview) {
        toast.error('Interview not found');
        return;
      }

      if (interview.status === 'COMPLETED') {
        toast.info('Interview is already marked as completed');
        return;
      }

      // Create payload with minimal required fields
      const payload = {
        id: interview.interviewId,
        candidateId: interview.candidateId,
        interviewerId: interview.interviewerId,
        dateTime: interview.dateTime,
        duration: interview.duration,
        type: interview.type,
        status: 'COMPLETED',
        priority: interview.priority,
        location: interview.location,
        round: interview.round,
        notes: interview.notes || '',
        feedback: interview.feedback ?
          `${interview.feedback}\nInterview marked as completed via UI.` :
          'Interview marked as completed via UI.',
      };

      await updateInterview(interviewId, payload);
      await fetchInterviews();
      toast.success('Interview marked as completed successfully!');
    } catch (error) {
      console.error('Error marking interview as completed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to mark interview as completed.';
      toast.error(errorMessage);
    }
  };

  const renderNestedInterviews = (candidate) => {
    return (
      <div className="nested-interview-list p-4">
        <h4 className="font-bold mb-2">Interviews</h4>
        {candidate.interviews.length > 0 ? (
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-2 text-left">Interview Title</th>
                <th className="p-2 text-left">Interviewer</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Date & Time</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Mark as Completed</th>
                {showAuditInfo && (
                  <>
                    <th className="p-2 text-left">Created By</th>
                    <th className="p-2 text-left">Created Date</th>
                    <th className="p-2 text-left">Last Modified By</th>
                    <th className="p-2 text-left">Last Modified Date</th>
                  </>
                )}
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidate.interviews.map((interview) => (
                <tr key={interview.interviewId} className="border-b">
                  <td className="p-2">{getDisplayValue(roundOptions, interview.round)}</td>
                  <td className="p-2">{interview.interviewerName}</td>
                  <td className="p-2">{getDisplayValue(interviewTypeOptions, interview.type)}</td>
                  <td className="p-2">
                    {interview.dateTime ? format(new Date(interview.dateTime), 'dd/MM/yyyy HH:mm', { locale: en }) : '-'}
                  </td>
                  <td className="p-2">{getInterviewStatusBadge(interview.status)}</td>
                  <td className="p-2">
                    <button
                      onClick={() => handleMarkInterviewCompleted(interview.interviewId)}
                      disabled={interview.status === 'COMPLETED'}
                      className={`btn ${interview.status === 'COMPLETED' ? 'btn-disabled' : 'btn-default'} action-button`}
                      title={interview.status === 'COMPLETED' ? 'Already completed' : 'Mark as completed'}
                    >
                      <CheckCircle className="action-icon" size={16} color={interview.status === 'COMPLETED' ? '#16A34A' : '#6B7280'} />
                      {interview.status === 'COMPLETED' ? 'Completed' : 'Complete'}
                    </button>
                  </td>
                  {showAuditInfo && (
                    <>
                      <td className="p-2">{interview.createdBy || 'SYSTEM'}</td>
                      <td className="p-2">{formatAuditDate(interview.createdDate)}</td>
                      <td className="p-2">{interview.lastModifiedBy || 'SYSTEM'}</td>
                      <td className="p-2">{formatAuditDate(interview.lastModifiedDate)}</td>
                    </>
                  )}
                  <td className="p-2">
                    <button
                      onClick={() => handleViewInterview(interview)}
                      className="btn btn-view action-button"
                    >
                      <Eye className="action-icon" size={16} />
                    </button>
                    <button
                      onClick={() => handleEditInterview(interview)}
                      className="btn btn-edit action-button"
                    >
                      <Edit className="action-icon" size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteInterview(interview.interviewId)}
                      className="btn btn-delete action-button"
                    >
                      <Trash2 className="action-icon" size={16} />
                    </button>
                    <button
                      onClick={() =>
                        handleSendEmail({
                          ...interview,
                          candidateName: `${candidate.firstName} ${candidate.lastName}`,
                          position: candidate.position,
                          email: candidate.email || '',
                        })
                      }
                      className="btn btn-default action-button"
                    >
                      <Mail className="action-icon" size={16} />
                    </button>
                    <button
                      onClick={() => handleReschedule(interview)}
                      className="btn btn-default action-button"
                    >
                      <RefreshCw className="action-icon" size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No interviews scheduled for this candidate.</p>
        )}
      </div>
    );
  };
  const getInterviewStatusBadge = (status) => {
    const config = {
      SCHEDULED: { className: 'status-pending', icon: Calendar, bgColor: 'rgba(245, 158, 11, 0.12)', color: '#92400E', border: '#F59E0B' },
      COMPLETED: { className: 'status-approved', icon: CheckCircle, bgColor: 'rgba(16, 185, 129, 0.12)', color: '#166534', border: '#22C55E' },
      CANCELLED: { className: 'status-rejected', icon: XCircle, bgColor: 'rgba(239, 68, 68, 0.12)', color: '#991B1B', border: '#EF4444' },
      POSTPONED: { className: 'status-default', icon: Clock, bgColor: 'rgba(125, 211, 252, 0.15)', color: '#0369A1', border: '#38BDF8' },
    };
    const { className, icon: Icon, bgColor, color, border } = config[status] || { className: 'status-default', icon: Clock, bgColor: 'rgba(156, 163, 175, 0.12)', color: '#374151', border: '#9CA3AF' };
    return (
      <span className={`status-badge ${className}`} style={{ backgroundColor: bgColor, color: color, border: `1px solid ${border}` }}>
        <Icon className="badge-icon" size={14} />
        {getDisplayValue(interviewStatusOptions, status)}
      </span>
    );
  };
  const totalPages = Math.ceil(filteredCandidates.length / rowsPerPage);
  const modalRef = useRef(null);
  const handleOfficeFilterChange = (e) => {
    const newOffice = e.target.value;
    setSelectedOfficeFilter(newOffice);
    setCurrentPage(1);
  };
  const [collapsedSections, setCollapsedSections] = useState({
    secondInterviews: true,
    thirdInterviews: true,
    waitingList: true,
    reservedList: true,
    notPlanned: true,
    pending: true,
    hired: true,
    declinedHR: true,
    declinedManager: true,
    candidateDeclined: true,
  });
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const addFooterWithPagination = (doc, currentPage, totalPages) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    doc.setFillColor(248, 250, 252);
    doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${currentPage} of ${totalPages} | AxoPeople`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('Candidate Management Report - Confidential', margin, pageHeight - 8);
    const today = new Date().toLocaleDateString();
    doc.text(`Generated: ${today}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  };
  // ✅ UPDATED: PDF Export — removed City, Status, Phone
  const generatePDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const today = new Date().toLocaleDateString('en-GB');

      // --- Draw Header Style (Matches HRWeeklyReport) ---
      const axoLogoUrl = '/logo1.png'; // Ensure this file exists in public folder
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);
      try {
        doc.addImage(marabesLogo, 'PNG', margin, 10, 35, 12);
      } catch (e) {
        console.warn("Marabes logo load failed", e);
      }
      if (axoBase64) {
        try {
          doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12);
        } catch (e) {
          console.warn("AXO logo add failed", e);
        }
      }

      // Blue Divider Line & Background
      doc.setDrawColor(63, 81, 181); // Primary Blue
      doc.setLineWidth(0.5);
      doc.line(margin, 25, pageWidth - margin, 25);
      doc.setFillColor(232, 245, 255); // Light Blue Background
      doc.rect(margin, 30, contentWidth, 14, 'F');
      const textY = 39;

      // Header Text
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${today}`, margin + 3, textY);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 58, 139); // Darker Blue
      doc.text('Candidate Management Report', pageWidth / 2, textY, { align: 'center' });

      // Report Details
      const tabName = getTabDisplayName(activeTab);
      const recordCount = filteredCandidates.length;
      const reportTitle = `${tabName} Candidate Details (${recordCount} records)`;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 55, { align: 'center' });

      let tableData;
      let headColumns;

      if (activeTab === 'approved') {
        tableData = filteredCandidates.map(candidate => [
          `${candidate.firstName || ''} ${candidate.lastName || ''}`,
          candidate.position || 'N/A',
          candidate.department?.name || 'N/A',
          candidate.office || 'N/A',
          candidate.level || 'N/A',
        ]);
        headColumns = [['Candidate Name', 'Position', 'Department', 'Office', 'Level']];
      } else if (activeTab === 'planned') {
        tableData = filteredCandidates.map(candidate => [
          `${candidate.firstName || ''} ${candidate.lastName || ''}`,
          candidate.position || 'N/A',
          candidate.department?.name || 'N/A',
          candidate.office || 'N/A',
        ]);
        headColumns = [['Candidate Name', 'Position', 'Department', 'Office']];
      } else {
        tableData = filteredCandidates.map(candidate => [
          `${candidate.firstName || ''} ${candidate.lastName || ''}`,
          candidate.position || 'N/A',
          toPlainText(candidate.experience, 'N/A'),
          candidate.diploma || 'N/A',
          candidate.appliedFrom || 'N/A',
          candidate.office || 'N/A',
        ]);
        headColumns = [['Candidate Name', 'Position', 'Experience', 'Diploma', 'Applied From', 'Office']];
      }

      autoTable(doc, {
        startY: 65,
        head: headColumns,
        body: tableData,
        theme: 'striped',
        styles: { fontSize: 10, cellPadding: 5 },
        headStyles: {
          fillColor: [14, 165, 233],
          textColor: 255,
          fontSize: 11,
          fontStyle: 'bold'
        },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { horizontal: margin },
        didDrawPage: function (data) {
          // Add footer on each page
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(107, 114, 128);
          doc.setFont('helvetica', 'italic');
          doc.text('Candidate Management Report - Confidential', margin, pageHeight - 10);
          doc.text(`Page ${data.pageNumber} of ${data.pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Generated: ${today}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });

      doc.save(`candidates-${activeTab}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error("PDF generation failed", error);
      toast.error("Failed to generate PDF report.");
    } finally {
      setIsExporting(false);
    }
  };
  const generateExcel = async () => {
    setIsExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      const sheetName = 'Candidates';
      const tabName = getTabDisplayName(activeTab);
      const recordCount = filteredCandidates.length;
      const reportTitle = `${tabName} Candidate Details (${recordCount} records)`;

      // Create header and data array
      const excelData = [];

      // Add title rows
      excelData.push(['CANDIDATE MANAGEMENT REPORT']);
      excelData.push(['Generated:', new Date().toLocaleDateString()]);
      excelData.push(['Report:', reportTitle]);
      excelData.push([]); // Empty row

      // Add column headers
      const excelHeaders = ['Name', 'Position', 'Experience', 'Diploma', 'Applied From', 'Office'];
      if (activeTab === 'approved') {
        excelHeaders.push('Level');
      }
      excelHeaders.push('Description');
      excelData.push(excelHeaders);

      // Add candidate data
      filteredCandidates.forEach((candidate) => {
        const row = [
          `${candidate.firstName || ''} ${candidate.lastName || ''}`,
          candidate.position || 'N/A',
          toPlainText(candidate.experience, 'N/A'),
          candidate.diploma || 'N/A',
          candidate.appliedFrom || 'N/A',
          candidate.office || 'N/A',
        ];
        if (activeTab === 'approved') {
          row.push(candidate.level || 'N/A');
        }
        row.push(toPlainText(candidate.description, 'N/A'));
        excelData.push(row);
      });

      // Create worksheet from array of arrays
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Style the header row (row 4, index 4)
      const headerRow = 4;
      const numCols = excelHeaders.length;
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        fill: { fgColor: { rgb: "0EA5E9" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };

      // Apply header styling
      for (let C = 0; C < numCols; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: C });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = headerStyle;
      }

      // Apply borders to data rows
      const dataStartRow = headerRow + 1;
      const dataEndRow = headerRow + filteredCandidates.length;
      const dataCellStyle = {
        border: {
          top: { style: "thin", color: { rgb: "D1D5DB" } },
          bottom: { style: "thin", color: { rgb: "D1D5DB" } },
          left: { style: "thin", color: { rgb: "D1D5DB" } },
          right: { style: "thin", color: { rgb: "D1D5DB" } }
        },
        alignment: { vertical: "top", wrapText: true }
      };

      for (let R = dataStartRow; R <= dataEndRow; R++) {
        for (let C = 0; C < numCols; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          ws[cellAddress].s = dataCellStyle;
          // Make first column (Name) bold
          if (C === 0) {
            ws[cellAddress].s = {
              ...dataCellStyle,
              font: { bold: true }
            };
          }
        }
      }

      // Set column widths
      ws['!cols'] = [
        { wch: 25 }, // Name
        { wch: 20 }, // Position
        { wch: 15 }, // Experience
        { wch: 15 }, // Diploma
        { wch: 15 }, // Applied From
        { wch: 15 }, // Office
        { wch: 40 }, // Description
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate and download file
      XLSX.writeFile(wb, `candidates-${activeTab}-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Error generating Excel:', error);
      toast.error('Failed to generate Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  if (loading) {
    return (
      <div className="axo-page-loader">
        <div className="axo-page-loader__ring" />
        <p className="axo-page-loader__text">Loading candidates...</p>
      </div>
    );
  }
  // ✅ DASHBOARD CARD HELPER
  const renderDashboardCard = (label, value, icon, color, onClick, bgColor = 'white') => {
    let finalBgColor = bgColor;
    if (isDarkMode) {
      if (bgColor === 'white') {
        finalBgColor = 'rgba(31, 41, 55, 0.5)';
      } else {
        finalBgColor = bgColor.replace('0.6', '0.2').replace('rgb', 'rgba');
      }
    }

    return (
      <div
        onClick={onClick}
        className={`glass-card animated-card ${isDarkMode ? 'dark-mode-card' : ''}`}
        style={{
          padding: '1.25rem',
          borderRadius: '1rem',
          cursor: 'pointer',
          background: finalBgColor,
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : `1px solid rgba(255,255,255,0.4)`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{ position: 'absolute', top: '-40%', right: '-40%', width: '160%', height: '160%', background: color, opacity: 0.08, borderRadius: '50%' }}></div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#9ca3af' : '#6B7280', marginBottom: '0.25rem', fontWeight: '600' }}>{label}</p>
          <p style={{ fontSize: '1.55rem', fontWeight: '800', color: isDarkMode && (color === '#0c4a6e' || color === '#1e3a8a' || color === '#991B1B' || color === '#92400E') ? '#f9fafb' : color, margin: 0 }}>{value}</p>
        </div>
        <div style={{ position: 'absolute', top: '16px', right: '16px', opacity: 0.3 }}>
          {React.createElement(icon, { size: 28, color: isDarkMode && (color === '#0c4a6e' || color === '#1e3a8a' || color === '#991B1B' || color === '#92400E') ? '#9ca3af' : color })}
        </div>
      </div>
    );
  };
  return (
    <div style={{
      minHeight: '100vh',
      // padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: 'rgb(248 250 252 / 0%);',
    }}>
      <style>{`
@keyframes spin {
  to { transform: rotate(360deg); }
}
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
}
@keyframes slideIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
.animated-card {
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.animated-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(14, 165, 233, 0.18) !important;
}
.glass-card {
  background: ${isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'rgba(255, 255, 255, 0.78)'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: ${isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.6)'};
  border-radius: inherit;
}
.gradient-text {
  background: linear-gradient(135deg, ${THEME_PRIMARY} 0%, ${THEME_SECONDARY} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.65rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.status-approved { background: rgba(16, 185, 129, 0.12); color: #166534; border: 1px solid #22C55E; }
.status-rejected { background: rgba(239, 68, 68, 0.12); color: #991B1B; border: 1px solid #EF4444; }
.status-warning { background: rgba(245, 158, 11, 0.12); color: #92400E; border: 1px solid #F59E0B; }
.status-default { background: rgba(156, 163, 175, 0.12); color: #374151; border: 1px solid #9CA3AF; }
.tab-button-active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${THEME_PRIMARY};
  border-radius: 3px 3px 0 0;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
}
.modal-content {
  background: white;
  border-radius: 1.2rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
}
.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
`}</style>
      <ToastContainer />
      {/* Notifications */}
      {/* {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem 1.5rem',
          borderRadius: '0.75rem',
          background: 'linear-gradient(135deg, #FEE2E2, #FECACA)',
          color: '#991B1B',
          fontWeight: '600',
          boxShadow: '0 10px 25px rgba(239,68,68,0.2)',
          zIndex: 2100
        }}>
          <AlertCircle size={20} color="#991B1B" />
          {error}
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B' }}>
            <X size={18} />
          </button>
        </div>
      )} */}
      {/* Header */}
      {/* Header */}
      <PageHeader
        title="Candidate Management"
        subtitle="Track and manage your recruitment pipeline"
        actions={
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            {mainTab === 'overview' && (
              <>
                <select
                  value={selectedOfficeFilter}
                  onChange={handleOfficeFilterChange}
                  className="axo-filter-select"
                >
                  <option value="all">All Offices</option>
                  {officeOptions.map((office) => (
                    <option key={office} value={office}>{office}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setShowAddForm(true);
                    setEditingCandidate(null);
                    setFormData({
                      firstName: '',
                      lastName: '',
                      phone: '',
                      position: '',
                      departmentName: '',
                      office: '',
                      cin: '',
                      description: '',
                      city: '',
                      image: null,
                      resume: null,
                      status: 'Not Planned Yet',
                      level: '',
                      experience: '',
                      diploma: '',
                      studyField: '',
                      appliedFrom: '',
                      portfolioLink: '',
                      resumeTeamsLink: '',
                      internship: '',
                      skills: [],
                    });
                    setImageFile(null);
                    setResumeFile(null);
                    setError(null);
                  }}
                  className="animated-card"
                  style={{
                    padding: '0.6rem 1.1rem',
                    background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.6rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    boxShadow: `0 6px 18px ${THEME_PRIMARY}25`
                  }}
                >
                  <Plus size={16} /> Add Candidate
                </button>
                <button
                  onClick={deleteDeclinedCandidatesOlderThan6Months}
                  className="animated-card"
                  style={{
                    padding: '0.6rem 1.1rem',
                    background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
                    color: '#991B1B',
                    border: 'none',
                    borderRadius: '0.6rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem'
                  }}
                >
                  <Trash2 size={16} /> Clean Up Declined
                </button>
                <ExportButtons
                  onExportPDF={handleExportOverviewPDF}
                  isExporting={isExporting}
                />
              </>
            )}
            {mainTab === 'candidates' && (
              <>
                <ExportButtons
                  onExportPDF={generatePDF}
                  onExportExcel={generateExcel}
                  isExporting={isExporting}
                />
              </>
            )}
          </div>
        }
      />
      {/* Tabs */}
      <div className="glass-card" style={{
        borderRadius: '1rem',
        padding: '0',
        marginBottom: '1.4rem',
        boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.3)',
          padding: '0 1rem'
        }}>
          {['overview', 'candidates', 'overview_table'].map(tab => (
            <button
              key={tab}
              onClick={() => {
                setMainTab(tab);
                if (tab === 'overview_table') {
                  setActiveTab('all');
                }
              }}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                background: 'transparent',
                fontSize: '1rem',
                fontWeight: mainTab === tab ? '700' : '500',
                color: mainTab === tab ? THEME_PRIMARY : isDarkMode ? '#9ca3af' : '#6B7280',
                cursor: 'pointer',
                position: 'relative',
                outline: 'none',
              }}
            >
              {tab === 'overview_table' ? 'Overview Table' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              {mainTab === tab && (
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  height: '3px',
                  background: THEME_PRIMARY,
                  borderRadius: '3px 3px 0 0'
                }} />
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Overview Tab */}
      {mainTab === 'overview' && (
        <div ref={overviewRef}>
          {/* Top Section: Employee Workforce Card */}
          <Box sx={{ mb: 2 }}>
            <EmployeeCard isLoading={loading} />
          </Box>

          {/* --- Dashboard Cards Row 1 --- */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            marginBottom: '1.2rem'
          }}>
            {/* ✅ UPDATED: Click triggers "total_overview" filter */}
            {renderDashboardCard('Total Candidates', dashboardCardCounts.totalCandidates, Users, THEME_PRIMARY, () => handleCardClick('total_overview'))}

            {renderDashboardCard('Pending Interviews', dashboardCardCounts.pending, Clock, SKY_SECONDARY, () => handleCardClick('pending'))}
            {renderDashboardCard('Planned Interviews', dashboardCardCounts.planned, Calendar, '#16A34A', () => handleCardClick('planned'))}
            {renderDashboardCard('Reserved', dashboardCardCounts.reserved, Briefcase, '#8B5CF6', () => handleCardClick('reserved'))}

            {/* ✅ UPDATED: Click triggers "approved" which defaults to "Hired Pending" */}
            {renderDashboardCard('Hired (Onboarding)', dashboardCardCounts.hired, CheckCircle, '#166534', () => handleCardClick('approved'))}
          </div>

          {/* --- Dashboard Cards Row 2 --- */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            marginBottom: '1.4rem'
          }}>
            {renderDashboardCard('Not Planned Yet', dashboardCardCounts.notPlanned, Clock, '#0c4a6e', () => handleCardClick('notplanned'))}
            {renderDashboardCard('Not Attended', dashboardCardCounts.notAttended, XCircle, '#92400E', () => handleCardClick('notattended'))}
            {renderDashboardCard('Not Planned-Old', dashboardCardCounts.notPlannedOld, XCircle, '#1e3a8a', () => handleCardClick('notplanned', 'old'), 'rgba(239, 246, 255, 0.6)')}
            {renderDashboardCard('Not Planned-Recent', dashboardCardCounts.notPlannedRecent, Calendar, '#1e3a8a', () => handleCardClick('notplanned', 'recent'), 'rgba(239, 246, 255, 0.6)')}

            {/* This count is now filtered by current year via useMemo */}
            {renderDashboardCard('Declined', dashboardCardCounts.declined, XCircle, '#991B1B', () => handleCardClick('rejected'), 'rgba(254, 242, 242, 0.6)')}
          </div>

          {/* --- Charts Section --- */}
          <div ref={chartsSectionRef} style={{ marginTop: '1.4rem' }}>

            {/* ✅ NEW: Year Filter for Charts */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="axo-filter-select"
                style={{ width: '150px' }}
              >
                <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginBottom: '1.2rem' }}>
              <MonthlyApplicationsChart
                currentData={selectedChartApplicationData} // ✅ PASS THE FILTERED DATA
                departments={departments}
                selectedOffice={selectedOfficeFilter}
              />
              <StatusDistribution
                currentData={selectedChartStatusData}
              />
            </div>

            {/* --- Analytics Dashboard --- */}
            <div className="glass-card" style={{ padding: '1.4rem', borderRadius: '1rem', boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)' }}>
              <CandidateInterviewAnalyticsDashboard
                interviewsData={interviewsData.filter(i => {
                  const d = new Date(i.dateTime);
                  return !isNaN(d) && d.getFullYear() === selectedYear && baseFilteredCandidates.some(c => c.id === i.candidateId);
                })}
                candidateId={null}
                roundOptions={roundOptions}
                interviewStatusOptions={interviewStatusOptions}
                locationOptions={locationOptions}
              />
            </div>
          </div>
        </div>
      )}
      {mainTab === 'candidates' && (
        <div className="glass-card" ref={allCandidatesRef} style={{ padding: '1.6rem', borderRadius: '1rem', boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)' }}>
          <div className="table-header">
            <div className="table-header-content">
              <div>
                <h2 className="gradient-text" style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0 }}>All Candidates</h2>
                <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '0.25rem 0 0' }}>Manage and track all candidate applications</p>
              </div>
              <div className="table-controls" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                flexWrap: 'wrap'
              }}>
                <div className="axo-search-container">
                  <div className="axo-search-icon">
                    <Search size={18} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="axo-search-input"
                  />
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Users
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedFilter}
                    onChange={(e) => {
                      setSelectedFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Departments</option>
                    {Array.isArray(departments) && departments.length > 0 ? (
                      departments.map((dept) => (
                        <option key={dept.departmentId} value={dept.departmentId}>
                          {dept.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No departments available</option>
                    )}
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Calendar
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedAgeFilter}
                    onChange={(e) => {
                      setSelectedAgeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedAgeFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedAgeFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Ages</option>
                    <optgroup label="Age Ranges">
                      <option value="under-20">Under 20</option>
                      <option value="20-25">20 - 25</option>
                      <option value="26-30">26 - 30</option>
                      <option value="31-35">31 - 35</option>
                      <option value="36-40">36 - 40</option>
                      <option value="41-50">41 - 50</option>
                      <option value="above-50">50+</option>
                      <option value="not-specified">Not Specified</option>
                    </optgroup>
                    {availableAges.length > 0 && (
                      <optgroup label="Exact Age">
                        {availableAges.map((age) => (
                          <option key={age} value={`exact-${age}`}>{age} years old</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <MapPin
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedOfficeFilter}
                    onChange={(e) => setSelectedOfficeFilter(e.target.value)}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedOfficeFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedOfficeFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Offices</option>
                    {officeOptions.map((office) => (
                      <option key={office} value={office}>{office}</option>
                    ))}
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Shield
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={activeTab}
                    onChange={(e) => {
                      const nextTab = e.target.value;
                      setActiveTab(nextTab);
                      setNotPlannedSubFilter(null);
                      setDeclinedSubFilter(null);
                      setPlannedSubFilter(null);
                      if (nextTab !== 'approved') {
                        setSelectedLevelFilter('all');
                      }
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: activeTab !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: activeTab !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="notplanned">Not Planned</option>
                    <option value="notattended">Not Attended</option>
                    <option value="pending">Pending</option>
                    <option value="planned">Planned Interviews</option>
                    <option value="approved">Hired</option>
                    <option value="rejected">Declined </option>
                    <option value="reserved">Reserved</option>
                    <option value="postponed">Postponed</option>
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <TrendingUp
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedLevelFilter}
                    onChange={(e) => {
                      const newLvl = e.target.value;
                      setSelectedLevelFilter(newLvl);
                      if (newLvl !== 'all' && activeTab !== 'approved') {
                        setActiveTab('approved');
                        setHiredSubFilter('all');
                      }
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    title={activeTab !== 'approved' ? "Level filter applies to Hired status only. Selecting will switch to Hired candidates." : "Filter hired candidates by level"}
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedLevelFilter !== 'all' ? THEME_PRIMARY : undefined,
                      color: selectedLevelFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedLevelFilter !== 'all' ? '600' : undefined
                    }}
                  >
                    <option value="all">{activeTab === 'approved' ? 'All Levels' : 'Level (Hired Only)'}</option>
                    <option value="HIGH">🟢 Level: High</option>
                    <option value="AVERAGE">🟠 Level: Average</option>
                    <option value="LOW">🟡 Level: Low</option>
                  </select>
                </div>
                {activeTab === 'notplanned' && (
                  <>
                    <button
                      onClick={() => {
                        setNotPlannedSubFilter(notPlannedSubFilter === 'recent' ? null : 'recent');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: notPlannedSubFilter === 'recent' ? `2px solid ${THEME_PRIMARY}` : isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                        background: notPlannedSubFilter === 'recent' ? (isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(240, 249, 255, 0.8)') : (isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'white'),
                        color: isDarkMode ? '#f9fafb' : 'inherit',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Not Planned-Recent
                    </button>
                    <button
                      onClick={() => {
                        setNotPlannedSubFilter(notPlannedSubFilter === 'old' ? null : 'old');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: notPlannedSubFilter === 'old' ? `2px solid ${THEME_PRIMARY}` : isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                        background: notPlannedSubFilter === 'old' ? (isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(240, 249, 255, 0.8)') : (isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'white'),
                        color: isDarkMode ? '#f9fafb' : 'inherit',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Not Planned-Old
                    </button>
                  </>
                )}
                {activeTab === 'approved' && (
                  <>
                    <button
                      onClick={() => {
                        setHiredSubFilter(hiredSubFilter === 'pending' ? null : 'pending');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: hiredSubFilter === 'pending' ? `2px solid ${THEME_PRIMARY}` : '1px solid #E5E7EB',
                        background: hiredSubFilter === 'pending' ? 'rgba(240, 249, 255, 0.8)' : 'white',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Hired Pending
                    </button>
                    <button
                      onClick={() => {
                        setHiredSubFilter(hiredSubFilter === 'hired' ? null : 'hired');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: hiredSubFilter === 'hired' ? `2px solid ${THEME_PRIMARY}` : '1px solid #E5E7EB',
                        background: hiredSubFilter === 'hired' ? 'rgba(240, 249, 255, 0.8)' : 'white',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Hired
                    </button>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.2rem 0.4rem',
                      borderRadius: '0.6rem',
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#F1F5F9',
                      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`,
                      flexWrap: 'nowrap'
                    }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: '700', color: isDarkMode ? '#94A3B8' : '#64748B', padding: '0 0.2rem' }}>
                        Level:
                      </span>
                      {['all', 'HIGH', 'AVERAGE', 'LOW'].map((lvl) => {
                        const isSelected = selectedLevelFilter === lvl;
                        let activeBg = THEME_PRIMARY;
                        let activeColor = '#fff';
                        let circleIcon = null;
                        if (lvl === 'HIGH') {
                          activeBg = '#10B981';
                          circleIcon = '🟢';
                        } else if (lvl === 'AVERAGE') {
                          activeBg = '#EA580C';
                          circleIcon = '🟠';
                        } else if (lvl === 'LOW') {
                          activeBg = '#CA8A04';
                          circleIcon = '🟡';
                        }
                        return (
                          <button
                            key={lvl}
                            onClick={() => {
                              setSelectedLevelFilter(lvl);
                              setCurrentPage(1);
                            }}
                            className="animated-card"
                            style={{
                              padding: '0.25rem 0.55rem',
                              borderRadius: '0.4rem',
                              border: isSelected ? `1.5px solid ${activeBg}` : '1px solid transparent',
                              background: isSelected ? activeBg : 'transparent',
                              color: isSelected ? activeColor : isDarkMode ? '#CBD5E1' : '#475569',
                              fontSize: '0.75rem',
                              fontWeight: isSelected ? '700' : '500',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              transition: 'all 0.15s ease',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {circleIcon && <span style={{ fontSize: '0.75rem', lineHeight: 1 }}>{circleIcon}</span>}
                            {lvl === 'all' ? 'All' : lvl}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                {activeTab === 'rejected' && (
                  <>
                    <button
                      onClick={() => {
                        setDeclinedSubFilter(declinedSubFilter === 'hr' ? null : 'hr');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: declinedSubFilter === 'hr' ? `2px solid ${THEME_PRIMARY}` : '1px solid #E5E7EB',
                        background: declinedSubFilter === 'hr' ? 'rgba(240, 249, 255, 0.8)' : 'white',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Declined by HR
                    </button>
                    <button
                      onClick={() => {
                        setDeclinedSubFilter(declinedSubFilter === 'manager' ? null : 'manager');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: declinedSubFilter === 'manager' ? `2px solid ${THEME_PRIMARY}` : '1px solid #E5E7EB',
                        background: declinedSubFilter === 'manager' ? 'rgba(240, 249, 255, 0.8)' : 'white',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Declined by Manager
                    </button>
                    <button
                      onClick={() => {
                        setDeclinedSubFilter(declinedSubFilter === 'candidate' ? null : 'candidate');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: declinedSubFilter === 'candidate' ? `2px solid ${THEME_PRIMARY}` : '1px solid #E5E7EB',
                        background: declinedSubFilter === 'candidate' ? 'rgba(240, 249, 255, 0.8)' : 'white',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Candidate Declined Offer
                    </button>
                  </>
                )}
                {activeTab === 'planned' && (
                  <>
                    <button
                      onClick={() => {
                        setPlannedSubFilter(plannedSubFilter === 'first' ? null : 'first');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: plannedSubFilter === 'first' ? `2px solid ${THEME_PRIMARY}` : isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                        background: plannedSubFilter === 'first' ? (isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(240, 249, 255, 0.8)') : (isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'white'),
                        color: isDarkMode ? '#f9fafb' : 'inherit',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      1st Interview
                    </button>
                    <button
                      onClick={() => {
                        setPlannedSubFilter(plannedSubFilter === 'second' ? null : 'second');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: plannedSubFilter === 'second' ? `2px solid ${THEME_PRIMARY}` : isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                        background: plannedSubFilter === 'second' ? (isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(240, 249, 255, 0.8)') : (isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'white'),
                        color: isDarkMode ? '#f9fafb' : 'inherit',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      2nd Interview
                    </button>
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Filter by Date"
                      value={selectedDateFilter}
                      onChange={(date) => setSelectedDateFilter(date)}
                      format="dd-MM-yyyy"
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          size: "small",
                          placeholder: "DD-MM-YYYY",
                          inputProps: { readOnly: true },
                          style: {
                            width: '150px',
                            background: 'var(--card-bg, white)',
                            borderRadius: '0.6rem',
                            border: '1px solid #E5E7EB',
                            fontSize: '0.9rem'
                          }
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {selectedDateFilter && (
                    <button
                      onClick={() => setSelectedDateFilter(null)}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                      title="Clear date filter"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <button
                  className="animated-card glass-card"
                  onClick={() => setShowAuditInfo(!showAuditInfo)}
                  style={{
                    padding: '0.5rem 0.8rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  <Clock size={16} color={THEME_PRIMARY} />
                  {showAuditInfo ? 'Hide' : 'Show'} Audit Info
                </button>
                {/* ✅ REFRESH BUTTON */}
                <button
                  className="animated-card glass-card"
                  onClick={handleRefresh}
                  title="Refresh Data"
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RefreshCw size={16} color={THEME_PRIMARY} />
                </button>
                {hasActiveCandidateFilters && (
                  <button
                    onClick={resetCandidateFilters}
                    className="animated-card glass-card"
                    style={{
                      padding: '0.5rem 0.8rem',
                      border: '1px solid #EF4444',
                      borderRadius: '0.6rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#EF4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    title="Reset all filters"
                  >
                    <RotateCcw size={14} />
                    Reset Filters
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="table-content" style={{ marginTop: '1.2rem' }}>
            {viewMode === 'table' ? (
              <Table1_2
                data={paginatedCandidates}
                profileImages={profileImages}
                columns={[
                  {
                    header: 'Picture',
                    key: 'image',
                    render: (item) => (
                      <div className="candidate-info">
                        {profileImages[item.id] ? (
                          <img
                            src={profileImages[item.id]}
                            alt={`${item.firstName} ${item.lastName}'s profile`}
                            className="candidate-avatar"
                            style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : (
                          <div className="candidate-avatar" style={{
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                            color: 'white',
                            borderRadius: '50%'
                          }}>
                            {item.firstName.charAt(0)}{item.lastName.charAt(0)}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    header: 'Candidate',
                    key: 'firstName',
                    render: (item) => (
                      <div className="candidate-info">
                        <div>
                          <div className="candidate-name" style={{ fontWeight: '700', color: '#111827' }}>
                            {item.firstName} {item.lastName}
                          </div>
                          {/* <div className="candidate-cin" style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                            CIN: {item.cin}
                          </div> */}
                        </div>
                      </div>
                    ),
                  },
                  {
                    header: 'Age',
                    key: 'age',
                    onClick: () => handleSort('age'),
                    sortIcon: sortConfig.key === 'age' && (sortConfig.direction === 'ascending' ? <ChevronUp size={16} /> : <ChevronDown size={16} />),
                    render: (item) => (
                      <span style={{ color: '#374151', fontWeight: '600' }}>
                        {item.age != null && item.age !== '' ? item.age : '-'}
                      </span>
                    ),
                  },
                  {
                    header: 'Position',
                    key: 'position',
                    render: (item) => (
                      <div className="position-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Briefcase size={16} color={THEME_PRIMARY} />
                        <span className="position-text" style={{ color: '#374151' }}>{item.position}</span>
                      </div>
                    ),
                  },
                  // {
                  //   header: 'Diplomat',
                  //   key: 'diplomat',
                  //   render: (item) => (
                  //     <span style={{ color: '#374151' }}>{item.diplomat || '-'}</span>
                  //   ),
                  // },
                  {
                    header: 'Experience',
                    key: 'experience',
                    render: (item) => (
                      <div
                        style={{ color: '#374151', fontSize: '0.875rem', lineHeight: '1.5' }}
                        dangerouslySetInnerHTML={{ __html: item.experience || '-' }}
                      />
                    ),
                  },
                  {
                    header: 'Status',
                    key: 'status',
                    render: (item) => {
                      const { className, icon: Icon, bgColor, color, border } = getStatusBadgeStyles(item.status);
                      return (
                        <div className="position-info" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                          <span className={`status-badge ${className}`} style={{ backgroundColor: bgColor, color: color, border: `1px solid ${border}` }}>
                            <Icon className="badge-icon" size={14} />
                            {item.status}
                          </span>
                          {item.status === 'Hired' && (
                            item.level ? (
                              renderCandidateLevelBadge(item.level, isDarkMode)
                            ) : (
                              <span style={{ fontSize: '0.72rem', color: isDarkMode ? '#9ca3af' : '#9ca3af', fontStyle: 'italic', paddingLeft: '0.2rem' }}>
                                No level set
                              </span>
                            )
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    header: 'City',
                    key: 'city',
                    render: (item) => (
                      <div className="location-info" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#4B5563' }}>
                        <MapPin size={14} color="#6B7280" />
                        {item.city}
                      </div>
                    ),
                  },
                  {
                    header: 'Phone',
                    key: 'phone',
                    render: (item) => <span style={{ color: '#374151' }}>{item.phone}</span>
                  },
                  {
                    header: 'Description',
                    key: 'description',
                    render: (item) => (
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: '#374151'
                        }}
                        dangerouslySetInnerHTML={{ __html: item.description || '-' }}
                      />
                    ),
                  },
                  ...(showAuditInfo ? [
                    {
                      header: 'Created By',
                      key: 'createdBy',
                      onClick: () => handleSort('createdBy'),
                      sortIcon: sortConfig.key === 'createdBy' && (sortConfig.direction === 'ascending' ? <ChevronUp size={16} /> : <ChevronDown size={16} />),
                      render: (item) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151' }}>
                          <User size={16} color="#6B7280" />
                          {item.createdBy || 'SYSTEM'}
                        </div>
                      ),
                    },
                    {
                      header: 'Created Date',
                      key: 'createdDate',
                      onClick: () => handleSort('createdDate'),
                      sortIcon: sortConfig.key === 'createdDate' && (sortConfig.direction === 'ascending' ? <ChevronUp size={16} /> : <ChevronDown size={16} />),
                      render: (item) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151' }}>
                          <Clock size={16} color="#6B7280" />
                          {formatAuditDate(item.createdDate)}
                        </div>
                      ),
                    },
                    {
                      header: 'Last Modified By',
                      key: 'lastModifiedBy',
                      onClick: () => handleSort('lastModifiedBy'),
                      sortIcon: sortConfig.key === 'lastModifiedBy' && (sortConfig.direction === 'ascending' ? <ChevronUp size={16} /> : <ChevronDown size={16} />),
                      render: (item) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151' }}>
                          <User size={16} color="#6B7280" />
                          {item.lastModifiedBy || 'SYSTEM'}
                        </div>
                      ),
                    },
                    {
                      header: 'Last Modified Date',
                      key: 'lastModifiedDate',
                      onClick: () => handleSort('lastModifiedDate'),
                      sortIcon: sortConfig.key === 'lastModifiedDate' && (sortConfig.direction === 'ascending' ? <ChevronUp size={16} /> : <ChevronDown size={16} />),
                      render: (item) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151' }}>
                          <Clock size={16} color="#6B7280" />
                          {formatAuditDate(item.lastModifiedDate)}
                        </div>
                      ),
                    },
                  ] : []),
                  {
                    header: 'Actions',
                    key: 'actions',
                    render: (item) => (
                      <div className="actions" style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="animated-card"
                          style={{
                            padding: '0.4rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #E5E7EB',
                            background: 'var(--card-bg, white)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <Eye size={16} color="#4B5563" />
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="animated-card"
                          style={{
                            padding: '0.4rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #E5E7EB',
                            background: 'var(--card-bg, white)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <Edit size={16} color="#4B5563" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="animated-card"
                          style={{
                            padding: '0.4rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #EF4444',
                            background: 'var(--card-bg, white)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <Trash2 size={16} color="#EF4444" />
                        </button>
                        <button
                          onClick={() => handleSchedule(item)}
                          className="animated-card"
                          style={{
                            padding: '0.4rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #E5E7EB',
                            background: 'var(--card-bg, white)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          <Calendar size={16} color="#4B5563" />
                        </button>
                      </div>
                    ),
                  },
                ]}
                rowExpansion={renderNestedInterviews}
              />
            ) : (
              <div className="cards-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1.2rem',
                marginTop: '1rem'
              }}>
                {paginatedCandidates.map((candidate) => (
                  <div key={candidate.id} className="glass-card animated-card" style={{
                    padding: '1.4rem',
                    borderRadius: '1rem',
                    boxShadow: '0 6px 18px rgba(14, 165, 233, 0.06)'
                  }}>
                    <div className="candidate-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div className="candidate-info" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {profileImages[candidate.id] ? (
                          <img
                            src={profileImages[candidate.id]}
                            alt={`${candidate.firstName} ${candidate.lastName}'s profile`}
                            className="candidate-avatar"
                            style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="candidate-avatar" style={{
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                            color: 'white',
                            borderRadius: '50%'
                          }}>
                            {candidate.firstName.charAt(0)}{candidate.lastName.charAt(0)}
                          </div>
                        )}
                        <div>
                          <h3 className="candidate-name" style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{candidate.firstName} {candidate.lastName}</h3>
                          <p className="candidate-role" style={{ fontSize: '0.9rem', color: '#6B7280', margin: '0.2rem 0 0' }}>{candidate.position}</p>
                        </div>
                      </div>
                      <button className="more-button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        <MoreHorizontal size={18} color="#9CA3AF" />
                      </button>
                    </div>
                    <div className="candidate-details" style={{ marginBottom: '1.2rem' }}>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Status</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {getStatusBadge(candidate.status)}
                          {candidate.status === 'Hired' && candidate.level && (
                            renderCandidateLevelBadge(candidate.level, isDarkMode)
                          )}
                        </div>
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Diploma</span>
                        <span className="detail-value" style={{ fontSize: '0.95rem', color: '#111827' }}>{candidate.diploma || '-'}</span>
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Experience</span>
                        <div
                          className="detail-value"
                          style={{ fontSize: '0.95rem', color: '#111827', lineHeight: '1.5' }}
                          dangerouslySetInnerHTML={{ __html: candidate.experience || '-' }}
                        />
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Applied From</span>
                        <span className="detail-value" style={{ fontSize: '0.95rem', color: '#111827' }}>{candidate.appliedFrom || '-'}</span>
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Office</span>
                        <span className="detail-value location-info" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.95rem', color: '#111827' }}>
                          <MapPin size={12} color="#6B7280" />
                          {candidate.office}
                        </span>
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>City</span>
                        <span className="detail-value location-info" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.95rem', color: '#111827' }}>
                          <MapPin size={12} color="#6B7280" />
                          {candidate.city}
                        </span>
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Phone</span>
                        <span className="detail-value" style={{ fontSize: '0.95rem', color: '#111827' }}>{candidate.phone}</span>
                      </div>
                      <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Description</span>
                        <div
                          className="detail-value"
                          style={{
                            fontSize: '0.9rem',
                            color: '#374151',
                            textAlign: 'right',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '65%'
                          }}
                          dangerouslySetInnerHTML={{ __html: candidate.description || '-' }}
                        />
                      </div>
                      {showAuditInfo && (
                        <>
                          <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                            <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Created By</span>
                            <span className="detail-value" style={{ fontSize: '0.9rem', color: '#374151' }}>{candidate.createdBy || 'SYSTEM'}</span>
                          </div>
                          <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                            <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Created Date</span>
                            <span className="detail-value" style={{ fontSize: '0.9rem', color: '#374151' }}>{formatAuditDate(candidate.createdDate)}</span>
                          </div>
                          <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                            <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Last Modified By</span>
                            <span className="detail-value" style={{ fontSize: '0.9rem', color: '#374151' }}>{candidate.lastModifiedBy || 'SYSTEM'}</span>
                          </div>
                          <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                            <span className="detail-label" style={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: '600' }}>Last Modified Date</span>
                            <span className="detail-value" style={{ fontSize: '0.9rem', color: '#374151' }}>{formatAuditDate(candidate.lastModifiedDate)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="candidate-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleViewDetails(candidate)}
                        className="animated-card"
                        style={{
                          padding: '0.45rem 0.9rem',
                          border: '1px solid #E5E7EB',
                          borderRadius: '0.6rem',
                          background: 'var(--card-bg, white)',
                          color: '#374151',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}
                        title="View Details"
                      >
                        <Eye size={14} />
                        View
                      </button>
                      <button
                        onClick={() => handleEdit(candidate)}
                        className="animated-card"
                        style={{
                          padding: '0.45rem 0.9rem',
                          border: '1px solid #E5E7EB',
                          borderRadius: '0.6rem',
                          background: 'var(--card-bg, white)',
                          color: '#374151',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}
                        title="Edit Candidate"
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(candidate.id)}
                        className="animated-card"
                        style={{
                          padding: '0.45rem 0.9rem',
                          border: '1px solid #EF4444',
                          borderRadius: '0.6rem',
                          background: 'var(--card-bg, white)',
                          color: '#EF4444',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}
                        title="Delete Candidate"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                      <button
                        onClick={() => handleSchedule(candidate)}
                        className="animated-card"
                        style={{
                          padding: '0.45rem 0.9rem',
                          border: '1px solid #E5E7EB',
                          borderRadius: '0.6rem',
                          background: 'var(--card-bg, white)',
                          color: '#374151',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}
                        title="Schedule Interview"
                      >
                        <Calendar size={14} />
                        Schedule
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {viewMode === 'table' && (
              <div className="pagination" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1.4rem',
                paddingTop: '1rem',
                borderTop: '1px solid rgba(255,255,255,0.4)'
              }}>
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="animated-card"
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    background: 'var(--card-bg, white)',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span className="pagination-info" style={{ fontSize: '0.95rem', color: '#6B7280', fontWeight: '600' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="animated-card"
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    background: 'var(--card-bg, white)',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}
            {activeTab === 'rejected' && autoDeletionHistory.current.length > 0 && (
              <div style={{
                marginTop: '20px',
                padding: '10px',
                background: 'rgba(249, 250, 251, 0.6)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <button
                  className="animated-card glass-card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <History size={16} color={THEME_PRIMARY} />
                  View Deletion History
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {mainTab === 'overview_table' && (
        <div className="glass-card" style={{ padding: '1.6rem', borderRadius: '1rem', boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)' }}>
          <div className="table-header">
            <div className="table-header-content">
              <div>
                <h2 className="gradient-text" style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0 }}>Candidate Overview Table</h2>
                <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '0.25rem 0 0' }}>Comprehensive grid view of all candidates with skills, sources, and status matrix</p>
              </div>
              <div className="table-controls" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                flexWrap: 'wrap'
              }}>
                <div className="axo-search-container">
                  <div className="axo-search-icon">
                    <Search size={18} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search matrix..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="axo-search-input"
                  />
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Users
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedFilter}
                    onChange={(e) => {
                      setSelectedFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Departments</option>
                    {Array.isArray(departments) && departments.length > 0 ? (
                      departments.map((dept) => (
                        <option key={dept.departmentId} value={dept.departmentId}>
                          {dept.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No departments available</option>
                    )}
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Calendar
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedAgeFilter}
                    onChange={(e) => {
                      setSelectedAgeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedAgeFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedAgeFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Ages</option>
                    <optgroup label="Age Ranges">
                      <option value="under-20">Under 20</option>
                      <option value="20-25">20 - 25</option>
                      <option value="26-30">26 - 30</option>
                      <option value="31-35">31 - 35</option>
                      <option value="36-40">36 - 40</option>
                      <option value="41-50">41 - 50</option>
                      <option value="above-50">50+</option>
                      <option value="not-specified">Not Specified</option>
                    </optgroup>
                    {availableAges.length > 0 && (
                      <optgroup label="Exact Age">
                        {availableAges.map((age) => (
                          <option key={age} value={`exact-${age}`}>{age} years old</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <MapPin
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedOfficeFilter}
                    onChange={(e) => setSelectedOfficeFilter(e.target.value)}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedOfficeFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedOfficeFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Offices</option>
                    {officeOptions.map((office) => (
                      <option key={office} value={office}>{office}</option>
                    ))}
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Shield
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={activeTab}
                    onChange={(e) => {
                      const nextTab = e.target.value;
                      setActiveTab(nextTab);
                      setNotPlannedSubFilter(null);
                      setDeclinedSubFilter(null);
                      setPlannedSubFilter(null);
                      if (nextTab !== 'approved') {
                        setSelectedLevelFilter('all');
                      }
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    style={{
                      paddingLeft: '38px',
                      borderColor: activeTab !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: activeTab !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="notplanned">Not Planned</option>
                    <option value="notattended">Not Attended</option>
                    <option value="pending">Pending</option>
                    <option value="planned">Planned Interviews</option>
                    <option value="approved">Hired</option>
                    <option value="rejected">Declined</option>
                    <option value="reserved">Reserved</option>
                    <option value="postponed">Postponed</option>
                  </select>
                </div>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <TrendingUp
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      color: '#1976d2',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    value={selectedLevelFilter}
                    onChange={(e) => {
                      const newLvl = e.target.value;
                      setSelectedLevelFilter(newLvl);
                      if (newLvl !== 'all' && activeTab !== 'approved') {
                        setActiveTab('approved');
                      }
                      setCurrentPage(1);
                    }}
                    className="axo-filter-select"
                    title={activeTab !== 'approved' ? "Level filter applies to Hired status only. Selecting will switch to Hired tab." : "Filter hired candidates by level"}
                    style={{
                      paddingLeft: '38px',
                      borderColor: selectedLevelFilter !== 'all' ? THEME_PRIMARY : undefined,
                      color: selectedLevelFilter !== 'all' ? THEME_PRIMARY : undefined,
                      fontWeight: selectedLevelFilter !== 'all' ? '600' : undefined
                    }}
                  >
                    <option value="all">{activeTab === 'approved' ? 'All Levels' : 'Level (Hired Only)'}</option>
                    <option value="HIGH">🟢 Level: High</option>
                    <option value="AVERAGE">🟠 Level: Average</option>
                    <option value="LOW">🟡 Level: Low</option>
                  </select>
                </div>
                {activeTab === 'approved' && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.2rem 0.4rem',
                    borderRadius: '0.6rem',
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#F1F5F9',
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`,
                    flexWrap: 'nowrap'
                  }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: isDarkMode ? '#94A3B8' : '#64748B', padding: '0 0.2rem' }}>
                      Level:
                    </span>
                    {['all', 'HIGH', 'AVERAGE', 'LOW'].map((lvl) => {
                      const isSelected = selectedLevelFilter === lvl;
                      let activeBg = THEME_PRIMARY;
                      let activeColor = '#fff';
                      let circleIcon = null;
                      if (lvl === 'HIGH') {
                        activeBg = '#10B981';
                        circleIcon = '🟢';
                      } else if (lvl === 'AVERAGE') {
                        activeBg = '#EA580C';
                        circleIcon = '🟠';
                      } else if (lvl === 'LOW') {
                        activeBg = '#CA8A04';
                        circleIcon = '🟡';
                      }
                      return (
                        <button
                          key={lvl}
                          onClick={() => {
                            setSelectedLevelFilter(lvl);
                            setCurrentPage(1);
                          }}
                          className="animated-card"
                          style={{
                            padding: '0.25rem 0.55rem',
                            borderRadius: '0.4rem',
                            border: isSelected ? `1.5px solid ${activeBg}` : '1px solid transparent',
                            background: isSelected ? activeBg : 'transparent',
                            color: isSelected ? activeColor : isDarkMode ? '#CBD5E1' : '#475569',
                            fontSize: '0.75rem',
                            fontWeight: isSelected ? '700' : '500',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.15s ease',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {circleIcon && <span style={{ fontSize: '0.75rem', lineHeight: 1 }}>{circleIcon}</span>}
                          {lvl === 'all' ? 'All' : lvl}
                        </button>
                      );
                    })}
                  </div>
                )}
                {activeTab === 'planned' && (
                  <>
                    <button
                      onClick={() => {
                        setPlannedSubFilter(plannedSubFilter === 'first' ? null : 'first');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: plannedSubFilter === 'first' ? `2px solid ${THEME_PRIMARY}` : isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                        background: plannedSubFilter === 'first' ? (isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(240, 249, 255, 0.8)') : (isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'white'),
                        color: isDarkMode ? '#f9fafb' : 'inherit',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      1st Interview
                    </button>
                    <button
                      onClick={() => {
                        setPlannedSubFilter(plannedSubFilter === 'second' ? null : 'second');
                        setCurrentPage(1);
                      }}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem 0.8rem',
                        border: plannedSubFilter === 'second' ? `2px solid ${THEME_PRIMARY}` : isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #E5E7EB',
                        background: plannedSubFilter === 'second' ? (isDarkMode ? 'rgba(14, 165, 233, 0.2)' : 'rgba(240, 249, 255, 0.8)') : (isDarkMode ? 'rgba(31, 41, 55, 0.5)' : 'white'),
                        color: isDarkMode ? '#f9fafb' : 'inherit',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      2nd Interview
                    </button>
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Filter by Date"
                      value={selectedDateFilter}
                      onChange={(date) => setSelectedDateFilter(date)}
                      format="dd-MM-yyyy"
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          size: "small",
                          placeholder: "DD-MM-YYYY",
                          inputProps: { readOnly: true },
                          style: {
                            width: '150px',
                            background: 'var(--card-bg, white)',
                            borderRadius: '0.6rem',
                            border: '1px solid #E5E7EB',
                            fontSize: '0.9rem'
                          }
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {selectedDateFilter && (
                    <button
                      onClick={() => setSelectedDateFilter(null)}
                      className="animated-card glass-card"
                      style={{
                        padding: '0.5rem',
                        borderRadius: '0.6rem',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                      title="Clear date filter"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <button
                  className="animated-card glass-card"
                  onClick={handleRefresh}
                  title="Refresh Data"
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <RefreshCw size={16} color={THEME_PRIMARY} />
                </button>
                {hasActiveCandidateFilters && (
                  <button
                    onClick={resetCandidateFilters}
                    className="animated-card glass-card"
                    style={{
                      padding: '0.5rem 0.8rem',
                      border: '1px solid #EF4444',
                      borderRadius: '0.6rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#EF4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    title="Reset all filters"
                  >
                    <RotateCcw size={14} />
                    Reset Filters
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="table-content" style={{ marginTop: '1.2rem' }}>
            <CandidateOverviewTable
              candidates={filteredCandidates}
              interviews={interviewsData}
              onCandidateUpdate={handleOverviewCandidateUpdate}
            />
          </div>
        </div>
      )}
      {/* Modals */}
      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className={`modal-content ${isDarkMode ? 'dark-mode-modal' : ''}`} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingCandidate(null);
                setImageFile(null);
                setResumeFile(null);
                setError(null);
              }}
              style={{
                position: 'absolute',
                top: '1.2rem',
                right: '1.2rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} />
            </button>
            <div style={{ padding: '2rem' }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: THEME_PRIMARY, marginBottom: '1.5rem' }}>
                {editingCandidate ? 'Edit Candidate' : 'Add New Candidate'}
              </h2>
              {error && (
                <div className="uf-form-alert-error">
                  <AlertTriangle size={18} />
                  {error}
                </div>
              )}
              <div style={{ display: 'grid', gap: '1.2rem' }}>
                {/* Form fields styled with unified form utilities */}
                <div className="uf-form-section">
                  <div className="uf-form-grid">
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        First Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="uf-form-input"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Last Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="uf-form-input"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Phone <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        className="uf-form-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Position <span className="required">*</span>
                      </label>
                      <select
                        className="uf-form-select"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      >
                        <option value="">Select Position</option>
                        {positionOptions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                      </select>
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Department <span className="required">*</span>
                      </label>
                      <select
                        className="uf-form-select"
                        value={formData.departmentName}
                        onChange={(e) => setFormData({ ...formData, departmentName: e.target.value })}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.departmentId} value={dept.departmentId}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Office <span className="required">*</span>
                      </label>
                      <select
                        className="uf-form-select"
                        value={formData.office}
                        onChange={(e) => setFormData({ ...formData, office: e.target.value })}
                      >
                        <option value="">Select Office</option>
                        {officeOptions.map(office => <option key={office} value={office}>{office}</option>)}
                      </select>
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        City
                      </label>
                      <input
                        type="text"
                        className="uf-form-input"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        CIN <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="uf-form-input"
                        value={formData.cin}
                        onChange={(e) => setFormData({ ...formData, cin: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Status <span className="required">*</span>
                      </label>
                      <select
                        className="uf-form-select"
                        value={formData.status}
                        onChange={(e) => {
                          const nextStatus = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            status: nextStatus,
                            level: nextStatus === 'Hired' ? prev.level : ''
                          }));
                        }}
                      >
                        <option value="">Select Status</option>
                        {candidateStatusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                      </select>
                    </div>
                    {formData.status === 'Hired' && (
                      <div className="uf-form-group">
                        <label className="uf-form-label">
                          Candidate Level <span className="required">*</span>
                        </label>
                        <select
                          className="uf-form-select"
                          value={formData.level || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))}
                          required
                          style={{
                            fontWeight: '600',
                            borderColor: formData.level ? THEME_PRIMARY : undefined
                          }}
                        >
                          <option value="">Select Level</option>
                          <option value="HIGH">🟢 HIGH</option>
                          <option value="AVERAGE">🟠 AVERAGE</option>
                          <option value="LOW">🟡 LOW</option>
                        </select>
                        <span style={{ fontSize: '0.75rem', color: isDarkMode ? '#9ca3af' : '#6B7280', marginTop: '0.25rem', display: 'block' }}>
                          Required for hired candidates
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.4rem', color: '#374151' }}>
                      Jobs (Experience)
                    </label>
                    <RichTextEditor
                      value={formData.experience || ''}
                      onChange={(val) => setFormData({ ...formData, experience: val })}
                    />
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.4rem', color: '#374151' }}>
                      Internship
                    </label>
                    <RichTextEditor
                      value={formData.internship || ''}
                      onChange={(val) => setFormData({ ...formData, internship: val })}
                    />
                  </div>

                  <div className="uf-form-grid" style={{ marginTop: '1rem' }}>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Portfolio Link
                      </label>
                      <input
                        type="url"
                        className="uf-form-input"
                        value={formData.portfolioLink}
                        onChange={(e) => setFormData({ ...formData, portfolioLink: e.target.value })}
                        placeholder="https://github.com/..."
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: isDarkMode ? '#e2e8f0' : '#475569', marginBottom: '0.5rem' }}>
                      Resume teams link
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: isDarkMode ? '#334155' : '#f8fafc', border: `1px solid ${isDarkMode ? '#475569' : '#e2e8f0'}`, borderRadius: '0.375rem', padding: '0.5rem 0.75rem' }}>
                      <Link size={16} color={isDarkMode ? '#94a3b8' : '#64748b'} style={{ marginRight: '0.5rem' }} />
                      <input
                        type="url"
                        placeholder="https://..."
                        style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', color: isDarkMode ? '#f1f5f9' : '#0f172a', fontSize: '0.875rem' }}
                        value={formData.resumeTeamsLink}
                        onChange={(e) => setFormData({ ...formData, resumeTeamsLink: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Diploma
                      </label>
                      <select
                        className="uf-form-select"
                        value={formData.diploma}
                        onChange={(e) => setFormData({ ...formData, diploma: e.target.value })}
                      >
                        <option value="">Select Diploma</option>
                        {diplomaOptions.map(diploma => <option key={diploma} value={diploma}>{diploma}</option>)}
                      </select>
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Study Field
                      </label>
                      <input
                        type="text"
                        className="uf-form-input"
                        placeholder="Study Field"
                        value={formData.studyField || ''}
                        onChange={(e) => setFormData({ ...formData, studyField: e.target.value })}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Applied From
                      </label>
                      <select
                        className="uf-form-select"
                        value={formData.appliedFrom}
                        onChange={(e) => setFormData({ ...formData, appliedFrom: e.target.value })}
                      >
                        <option value="">Select Applied From</option>
                        {appliedFromOptions.map(source => <option key={source} value={source}>{source}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.4rem', color: '#374151' }}>
                      Description
                    </label>
                    <RichTextEditor
                      value={formData.description || ''}
                      onChange={(val) => setFormData({ ...formData, description: val })}
                    />
                  </div>

                  {/* Candidate Skills Section */}
                  <div style={{
                    marginTop: '1.2rem',
                    padding: '1.2rem',
                    backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#f8fafc',
                    borderRadius: '0.75rem',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.95rem', fontWeight: '700', color: isDarkMode ? '#f8fafc' : '#1e293b', margin: 0, display: 'block' }}>
                          Candidate Skills
                        </label>
                        <p style={{ fontSize: '0.8rem', color: isDarkMode ? '#94a3b8' : '#64748b', margin: '0.2rem 0 0 0' }}>
                          Add and evaluate skills (e.g. Communication Skills) for this candidate
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            skills: [
                              ...(prev.skills || []),
                              { skillId: null, type: 'COMMUNICATION_SKILLS', description: '' }
                            ]
                          }));
                        }}
                        style={{
                          padding: '0.45rem 0.85rem',
                          background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          fontSize: '0.82rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          boxShadow: `0 2px 8px ${THEME_PRIMARY}30`
                        }}
                      >
                        <Plus size={15} /> Add Skill
                      </button>
                    </div>

                    {(!formData.skills || formData.skills.length === 0) ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '1.2rem',
                        backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                        borderRadius: '0.5rem',
                        border: `1px dashed ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontSize: '0.85rem'
                      }}>
                        No skills added yet. Click &quot;Add Skill&quot; to add evaluation skills.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {formData.skills.map((skill, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              gap: '0.75rem',
                              alignItems: 'flex-start',
                              backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.6)' : '#ffffff',
                              padding: '0.85rem',
                              borderRadius: '0.5rem',
                              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`
                            }}
                          >
                            <div style={{ flex: '0 0 190px' }}>
                              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.3rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                                Skill Type
                              </label>
                              <select
                                className="uf-form-select"
                                style={{ fontSize: '0.82rem', padding: '0.45rem 0.6rem' }}
                                value={skill.type || 'COMMUNICATION_SKILLS'}
                                onChange={(e) => {
                                  const updated = [...formData.skills];
                                  updated[index] = { ...updated[index], type: e.target.value };
                                  setFormData((prev) => ({ ...prev, skills: updated }));
                                }}
                              >
                                <option value="COMMUNICATION_SKILLS">Communication Skills</option>
                              </select>
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.3rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                                Description / Notes
                              </label>
                              <input
                                type="text"
                                className="uf-form-input"
                                style={{ fontSize: '0.82rem', padding: '0.45rem 0.6rem' }}
                                placeholder="e.g. Fluent English, clear oral communication..."
                                value={skill.description || ''}
                                onChange={(e) => {
                                  const updated = [...formData.skills];
                                  updated[index] = { ...updated[index], description: e.target.value };
                                  setFormData((prev) => ({ ...prev, skills: updated }));
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              title="Remove Skill"
                              onClick={() => {
                                const updated = formData.skills.filter((_, idx) => idx !== index);
                                setFormData((prev) => ({ ...prev, skills: updated }));
                              }}
                              style={{
                                marginTop: '1.35rem',
                                padding: '0.45rem',
                                background: isDarkMode ? '#7f1d1d' : '#fee2e2',
                                color: '#ef4444',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Trash2 size={16} color="#ef4444" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="uf-form-grid" style={{ marginTop: '1rem' }}>
                    <div className="uf-form-group">
                      <label className="uf-form-label">
                        Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        className="uf-form-file"
                        onChange={(e) => setImageFile(e.target.files[0])}
                      />
                    </div>
                    <div className="uf-form-group">
                      <label className="uf-form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Resume (PDF)</span>
                        {editingCandidate && editingCandidate.hasResume && (
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <a 
                              href="#" 
                              onClick={(e) => { e.preventDefault(); handleViewResume(editingCandidate.id); }} 
                              style={{ color: '#0EA5E9', fontSize: '0.8rem', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                            >
                              <Eye size={14} style={{ marginRight: '4px' }}/> View
                            </a>
                            <a 
                              href="#" 
                              onClick={(e) => { e.preventDefault(); handleDownloadResume(editingCandidate.id); }} 
                              style={{ color: '#0EA5E9', fontSize: '0.8rem', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                            >
                              <Download size={14} style={{ marginRight: '4px' }}/> Download
                            </a>
                          </div>
                        )}
                      </label>
                      
                      {editingCandidate && editingCandidate.hasResume && !resumeFile && (
                        <div style={{ padding: '0.5rem', backgroundColor: '#F1F5F9', borderRadius: '0.35rem', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.85rem', color: '#475569', display: 'flex', alignItems: 'center' }}>
                            <PictureAsPdf style={{ fontSize: 16, marginRight: '6px', color: '#EF4444' }} />
                            {editingCandidate.resumeFileName || `resume_${editingCandidate.firstName}_${editingCandidate.lastName}.pdf`}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#10B981', fontWeight: '600', backgroundColor: '#D1FAE5', padding: '2px 6px', borderRadius: '4px' }}>Uploaded</span>
                        </div>
                      )}

                      <input
                        type="file"
                        accept=".pdf"
                        className="uf-form-file"
                        onChange={(e) => setResumeFile(e.target.files[0])}
                      />
                      {editingCandidate && editingCandidate.hasResume && (
                        <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: '#64748B' }}>
                          Upload a new file to replace the existing resume.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {editingCandidate && (
                  <div className="uf-form-section">
                    <h3 className="uf-form-section-title">Audit Information</h3>
                    <div className="uf-form-grid">
                      <div className="uf-form-group">
                        <label className="uf-form-label" style={{ fontSize: '0.8rem' }}>Created By</label>
                        <p style={{ margin: 0, fontWeight: '600' }}>{formData.createdBy || 'SYSTEM'}</p>
                      </div>
                      <div className="uf-form-group">
                        <label className="uf-form-label" style={{ fontSize: '0.8rem' }}>Created Date</label>
                        <p style={{ margin: 0, fontWeight: '600' }}>{formatAuditDate(formData.createdDate)}</p>
                      </div>
                      <div className="uf-form-group">
                        <label className="uf-form-label" style={{ fontSize: '0.8rem' }}>Last Modified By</label>
                        <p style={{ margin: 0, fontWeight: '600' }}>{formData.lastModifiedBy || 'SYSTEM'}</p>
                      </div>
                      <div className="uf-form-group">
                        <label className="uf-form-label" style={{ fontSize: '0.8rem' }}>Last Modified Date</label>
                        <p style={{ margin: 0, fontWeight: '600' }}>{formatAuditDate(formData.lastModifiedDate)}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="uf-form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingCandidate(null);
                      setImageFile(null);
                      setResumeFile(null);
                      setError(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleFormSubmit}
                  >
                    <Save size={16} />
                    {editingCandidate ? 'Update Candidate' : 'Add Candidate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDetails && selectedCandidate && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className={`modal-content ${isDarkMode ? 'dark-mode-modal' : ''}`} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowDetails(false)}
              style={{
                position: 'absolute',
                top: '1.2rem',
                right: '1.2rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} />
            </button>

            <div style={{ padding: '2rem' }}>
              <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: THEME_PRIMARY, marginBottom: '1.5rem' }}>
                Candidate Details
              </h2>
              <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem' }}>
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt={`${selectedCandidate.firstName} ${selectedCandidate.lastName}'s profile`}
                    style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 6px 18px rgba(0,0,0,0.08)' }}
                  />
                ) : (
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '3rem',
                    fontWeight: '700',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
                  }}>
                    {selectedCandidate.firstName.charAt(0)}{selectedCandidate.lastName.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '700', margin: '0 0 0.3rem 0' }}>
                    {selectedCandidate.firstName} {selectedCandidate.lastName}
                  </h3>
                  <p style={{ fontSize: '1.05rem', color: '#6B7280', margin: '0 0 0.6rem 0' }}>
                    {selectedCandidate.position}
                  </p>
                  <p style={{ fontSize: '0.95rem', color: '#4B5563', margin: 0 }}>
                    CIN: {selectedCandidate.cin}
                  </p>
                  <div style={{ fontSize: '0.95rem', color: '#4B5563', margin: '0.4rem 0 0', lineHeight: '1.5' }}
                    dangerouslySetInnerHTML={{ __html: 'Experience: ' + (selectedCandidate.experience || '-') }}
                  />
                  <p style={{ fontSize: '0.95rem', color: '#4B5563', margin: '0.4rem 0 0' }}>
                    Diploma: {selectedCandidate.diploma || '-'}
                  </p>
                  <p style={{ fontSize: '0.95rem', color: '#4B5563', margin: '0.4rem 0 0' }}>
                    Applied From: {selectedCandidate.appliedFrom || '-'}
                  </p>
                </div>
              </div>


              <div className="glass-card" style={{ padding: '1.2rem', borderRadius: '0.8rem', marginBottom: '1.2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', color: '#374151' }}>Personal Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>Phone</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.phone || '-'}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>Office</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.office || '-'}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>City</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.city || '-'}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>CIN</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.cin || '-'}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>Status</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.status || '-'}</p>
                  </div>
                  {selectedCandidate.status === 'Hired' && (
                    <div>
                      <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>Candidate Level</label>
                      <div style={{ marginTop: '0.2rem' }}>
                        {selectedCandidate.level ? (
                          renderCandidateLevelBadge(selectedCandidate.level, isDarkMode)
                        ) : (
                          <p style={{ margin: 0, fontWeight: '600', color: '#9CA3AF', fontStyle: 'italic' }}>Not set</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                  {selectedCandidate.description && (
                    <div style={{ marginTop: '1rem' }}>
                      <label style={{ fontSize: '0.85rem', color: '#6B7280' }}>Description</label>
                      <div
                        style={{ margin: 0, fontWeight: '600', fontSize: '0.95rem', lineHeight: '1.6' }}
                        dangerouslySetInnerHTML={{ __html: selectedCandidate.description }}
                      />
                    </div>
                  )}
              </div>

              {/* Details View - Skills Section */}
              {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                <div className="glass-card" style={{ padding: '1.2rem', borderRadius: '0.8rem', marginBottom: '1.2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', color: '#374151' }}>Skills</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {selectedCandidate.skills.map((skill, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          padding: '0.5rem 0.8rem',
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC',
                          borderRadius: '0.5rem',
                          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0'}`
                        }}
                      >
                        <span style={{
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '0.35rem',
                          backgroundColor: 'rgba(14, 165, 233, 0.12)',
                          color: THEME_PRIMARY
                        }}>
                          {skill.type === 'COMMUNICATION_SKILLS' ? 'Communication Skills' : skill.type}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: isDarkMode ? '#F1F5F9' : '#334155', fontWeight: '500' }}>
                          {skill.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="glass-card" style={{ padding: '1.2rem', borderRadius: '0.8rem', marginBottom: '1.2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', color: '#374151' }}>Audit Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#6B7280' }}>Created By</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.createdBy || 'SYSTEM'}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#6B7280' }}>Created Date</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{formatAuditDate(selectedCandidate.createdDate)}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#6B7280' }}>Last Modified By</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{selectedCandidate.lastModifiedBy || 'SYSTEM'}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: '#6B7280' }}>Last Modified Date</label>
                    <p style={{ margin: 0, fontWeight: '600' }}>{formatAuditDate(selectedCandidate.lastModifiedDate)}</p>
                  </div>
                </div>
              </div>
              {/* ✅ NEW: STATUS HISTORY TIMELINE SECTION */}
              <div className="glass-card" style={{ padding: '1.2rem', borderRadius: '0.8rem', marginBottom: '1.2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <History size={20} color={THEME_PRIMARY} />
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, color: '#374151' }}>Status History</h3>
                </div>
                <StatusHistoryTimeline candidateId={selectedCandidate.id} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem' }}>
                {selectedCandidate.hasResume && (
                  <button
                    onClick={() => handleDownloadResume(selectedCandidate.id)}
                    className="animated-card"
                    style={{
                      padding: '0.6rem 1.2rem',
                      background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.6rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Download size={16} color="white" />
                    Download Resume
                  </button>
                )}
                <button
                  onClick={() => setShowDetails(false)}
                  style={{
                    padding: '0.6rem 1.2rem',
                    border: '1.5px solid #E5E7EB',
                    borderRadius: '0.6rem',
                    background: 'var(--card-bg, white)',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(isModalOpen || isViewModalOpen) && (
        <div className="modal-overlay">
          <div className={`modal-content ${isDarkMode ? 'dark-mode-modal' : ''}`}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingInterview ? 'Edit Interview' : isViewModalOpen ? 'View Interview' : 'Schedule Interview'}
              </h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setIsViewModalOpen(false);
                  setEditingInterview(null);
                }}
                className="close-button"
              >
                <X size={20} />
              </button>
            </div>

            {/* ✅ FULL INTERVIEW FORM CONTENT — THIS WAS MISSING */}
            <div className="interviews-form-section">
              {!isViewModalOpen && (
                <div className="interviews-form-grid">
                  <div className="interviews-form-group full-width">
                    <label className="interviews-form-label">Candidate</label>
                    <select
                      value={formDataInterview.candidateId}
                      onChange={(e) =>
                        setFormDataInterview((prev) => ({ ...prev, candidateId: e.target.value }))
                      }
                      className="interviews-form-select"
                    >
                      <option value="">Select Candidate</option>
                      {candidatesList
                        .filter((c) => !['Hired', 'Declined by HR', 'Declined by Manager', 'Not Attended', 'Candidate Declined the Offer'].includes(c.status))
                        .map((cand) => (
                          <option key={cand.id} value={cand.id}>
                            {cand.firstName} {cand.lastName}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="interviews-form-group full-width">
                    <label className="interviews-form-label">Interviewer</label>
                    <select
                      value={formDataInterview.interviewerId}
                      onChange={(e) =>
                        setFormDataInterview((prev) => ({ ...prev, interviewerId: e.target.value }))
                      }
                      className="interviews-form-select"
                    >
                      <option value="">Select Interviewer</option>
                      {interviewers.map((emp) => (
                        <option key={emp.employeeId} value={emp.employeeId}>
                          {emp.firstName} {emp.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Date & Time</label>
                    <input
                      type="datetime-local"
                      value={formDataInterview.dateTime}
                      onChange={(e) =>
                        setFormDataInterview((prev) => ({ ...prev, dateTime: e.target.value }))
                      }
                      className="interviews-form-input"
                      min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                    />
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Duration (minutes)</label>
                    <input
                      type="number"
                      value={formDataInterview.duration}
                      onChange={(e) =>
                        setFormDataInterview((prev) => ({
                          ...prev,
                          duration: parseInt(e.target.value) || 60,
                        }))
                      }
                      className="interviews-form-input"
                      min="1"
                    />
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Interview Type</label>
                    <select
                      value={formDataInterview.type}
                      onChange={(e) => setFormDataInterview((prev) => ({ ...prev, type: e.target.value }))}
                      className="interviews-form-select"
                    >
                      <option value="">Select Type</option>
                      {interviewTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Status</label>
                    <select
                      value={formDataInterview.status}
                      onChange={(e) => setFormDataInterview((prev) => ({ ...prev, status: e.target.value }))}
                      className="interviews-form-select"
                    >
                      <option value="">Select Status</option>
                      {interviewStatusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Priority</label>
                    <select
                      value={formDataInterview.priority}
                      onChange={(e) =>
                        setFormDataInterview((prev) => ({ ...prev, priority: e.target.value }))
                      }
                      className="interviews-form-select"
                    >
                      <option value="">Select Priority</option>
                      {priorityOptions.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Location</label>
                    <select
                      value={formDataInterview.location}
                      onChange={(e) =>
                        setFormDataInterview((prev) => ({ ...prev, location: e.target.value }))
                      }
                      className="interviews-form-select"
                    >
                      <option value="">Select Location</option>
                      {locationOptions.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Round</label>
                    <select
                      value={formDataInterview.round}
                      onChange={(e) => setFormDataInterview((prev) => ({ ...prev, round: e.target.value }))}
                      className="interviews-form-select"
                    >
                      <option value="">Select Round</option>
                      {roundOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="interviews-form-group full-width">
                    <label className="interviews-form-label">Notes</label>
                    <RichTextEditor
                      value={formDataInterview.notes || ''}
                      onChange={(val) => setFormDataInterview((prev) => ({ ...prev, notes: val }))}
                    />
                  </div>
                  <div className="interviews-form-group full-width">
                    <label className="interviews-form-label">Feedback</label>
                    <RichTextEditor
                      value={formDataInterview.feedback || ''}
                      onChange={(val) => setFormDataInterview((prev) => ({ ...prev, feedback: val }))}
                    />
                  </div>
                </div>
              )}

              {isViewModalOpen && (
                <div className="interviews-form-grid">
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Candidate Name</label>
                    <p className="interviews-form-text">{selectedInterview?.candidateName || 'N/A'}</p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Interviewer</label>
                    <p className="interviews-form-text">{selectedInterview?.interviewerName || 'N/A'}</p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Date & Time</label>
                    <p className="interviews-form-text">
                      {selectedInterview?.dateTime ? (
                        <DateFormatter date={selectedInterview.dateTime} />
                      ) : (
                        'N/A'
                      )}
                    </p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Duration (minutes)</label>
                    <p className="interviews-form-text">{selectedInterview?.duration || 'N/A'}</p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Interview Type</label>
                    <p className="interviews-form-text">
                      {selectedInterview?.type
                        ? getDisplayValue(interviewTypeOptions, selectedInterview.type)
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Status</label>
                    <p className="interviews-form-text">
                      {selectedInterview?.status
                        ? getDisplayValue(interviewStatusOptions, selectedInterview.status)
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Priority</label>
                    <p className="interviews-form-text">{selectedInterview?.priority || 'N/A'}</p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Location</label>
                    <p className="interviews-form-text">{selectedInterview?.location || 'N/A'}</p>
                  </div>
                  <div className="interviews-form-group">
                    <label className="interviews-form-label">Round</label>
                    <p className="interviews-form-text">
                      {selectedInterview?.round
                        ? getDisplayValue(roundOptions, selectedInterview.round)
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="interviews-form-group full-width">
                    <label className="interviews-form-label">Notes</label>
                    <div className="interviews-form-text" dangerouslySetInnerHTML={{ __html: selectedInterview?.notes || 'N/A' }} />
                  </div>
                  <div className="interviews-form-group full-width">
                    <label className="interviews-form-label">Feedback</label>
                    <div className="interviews-form-text" dangerouslySetInnerHTML={{ __html: selectedInterview?.feedback || 'N/A' }} />
                  </div>
                </div>
              )}

              {!isViewModalOpen && (
                <div className="interviews-form-actions">
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setEditingInterview(null);
                    }}
                    className="button button-secondary"
                  >
                    Cancel
                  </button>
                  <button onClick={handleInterviewFormSubmit} className="button button-primary">
                    <Save className="icon" /> Save
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

  )
};

export default Candidates;
